import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { InvoiceShort } from './models/invoice/invoice-short';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { InvoiceStatus } from './enums/invoice-status';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { SidebarType } from './enums/sidebar-type';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, take, } from 'rxjs/operators';
import { UserService } from 'src/app/data/service/user.service';
import { User } from './models/user/user';
import { ErrorsService } from 'src/app/data/service/errors.service';
import { ErrorRecord } from './models/error/error-record';
import { ActivatedRoute, Router } from '@angular/router';
import { BookkeepingFilter } from './models/filter/bookkeeping-filter';
import { Location } from '@angular/common';
import { InboxInvoices } from './models/invoice/inbox-invoices';
import { SearchService } from 'src/app/data/service/search.service';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { SkeletonColumn } from 'src/app/shared/components/list-skeleton/list-skeleton.component';
import { dateToString } from 'src/app/core/util/date-converter';
import { Title } from '@angular/platform-browser';
import { SortTableParameters } from './models/filter/sort-table-parameters';
import { saveAs } from 'file-saver';
import { Tag } from 'src/app/data/model/tag';
import { TagsService } from 'src/app/data/service/tags.service';
import { TagToInvoices } from './models/tag/tag-to-invoice';
import { ArchiveRequest, InvoiceKey } from './models/invoice/archive-request';
import { DeletedTagFromInvoice } from './models/tag/deleted-tag';
import { TrackingCategory } from './models/tracking-categories.ts/trackingCategory';
import { BankAccountService } from 'src/app/data/service/bank-account.service';
import { PaymentAccount } from './models/account/payment-account';
import { BookkeepingFilterComponent } from './components/bookkeeping-filter/bookkeeping-filter.component';
import { ErrorRecordType } from './models/error/error-record-type';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ScreenBrakepoints } from 'src/app/data/model/screen-brakepoints';
import { SharedService } from 'src/app/core/service/shared.service';
import { ManageIntercom } from '../../core/util/manage-intercom';
import { FilterService } from 'src/app/data/service/filter.service';
import { ReportFilter } from 'src/app/data/model/reportFilter';
import * as $ from "jquery";
import { InboxFilterComponent } from './inbox-filter/inbox-filter.component';
import { describeInboxCriteria, iconForInboxConditions, inboxCriteriaToConditions, inboxDescriptionFromCriteria, InboxConditionField, statusesFromCriteria, stripPanelParams } from './inbox-filter/inbox-filter-condition';

/** The Inbox title picker's status views. For Review is the default. */
type InboxStatusViewKey = 'for-review' | 'processed' | 'all';

interface InboxStatusView {
  key: InboxStatusViewKey;
  name: string;
  blurb: string;
  icon: string;
}

interface InboxSavedFilterCard {
  id: string;
  name: string;
  icon: string;
  rule: string;
  blurb: string;
  active: boolean;
  filter: ReportFilter;
}
import { ConfirmationDialogService } from 'src/app/shared/confirmation-dialog/confirmation-dialog.service';
import { InboxInvoicesStorageService } from './services/inbox-invoices-storage.service';
import { environment } from 'src/environments/environment';
import { PageSidebarComponent } from '../shared/components/page-sidebar/page-sidebar.component';
import { Invoice } from './models/invoice/invoice';
import { CreditNoteDTO } from './models/credit-note/credit-note';
import { BankTransactionDTO } from './models/expense/bank-transactions';
import { AccountingIntegration } from 'src/app/data/enums/accounting-integration';
import { CreatedInvoiceWithLines } from './models/invoice/created-invoice-with-lines';
import { TableComponent } from './components/table/table.component';
import { SearchFilterConfig, FilterGroup, MultipleFiltersState } from 'src/app/shared/components/search-filter-toolbar/search-filter-toolbar.component';
import moment from 'moment';
import { inProcessingRecord } from './data/in-processing-record';
import { RecognizedInvoiceService } from './services/recognized-invoice.service';
import { AppConfigService } from 'src/app/appConfig.service';

type ColumnOption = { key: string; label: string };

/** SD-1235: the Inbox table's columns, as its loading skeleton draws them. */
const INBOX_SKELETON_COLUMNS: SkeletonColumn[] = [
  { width: '40px', kind: 'check' },
  { label: 'Supplier', width: 'minmax(180px, 2.2fr)', kind: 'entity' },
  { label: 'Date', width: '96px', kind: 'date', hideOnMobile: true },
  { label: 'Type', width: '110px', kind: 'text', hideOnMobile: true },
  { label: 'Category', width: 'minmax(160px, 1.6fr)', kind: 'text', hideOnMobile: true },
  { label: 'Submitter', width: 'minmax(120px, 1fr)', kind: 'text', hideOnMobile: true },
  { label: 'Tags', width: 'minmax(100px, 1fr)', kind: 'pill', hideOnMobile: true },
  { label: 'Total', width: '120px', kind: 'amount' },
  { label: 'Actions', width: '120px', kind: 'pill', align: 'center', hideOnMobile: true },
];

@Component({
  selector: 'app-bookkeeping',
  templateUrl: './bookkeeping.component.html',
  styleUrls: ['./bookkeeping.component.scss']
})
export class BookkeepingComponent extends BaseComponent implements OnInit, AfterViewInit {
  private _searchTerms = new Subject<string>();
  // Free-text inbox search is bound to (input), so it fires on every keystroke.
  // We push each value through this subject and debounce it, so the actual
  // filter/API call only runs once the user pauses typing — no more a request
  // (and list refresh) per character. Paired with the single-flight cancel in
  // the fetch methods (inboxListSub), this gives one clean result per search.
  private _inboxSearch$ = new Subject<string>();
  searchText: string = '';
  inProcessingInvoices: InvoiceShort[] = [];
  processedInvoices: InvoiceShort[] = [];
  storedInvoices: InvoiceShort[] = [];
  storedErrorInvoices: ErrorRecord[] = [];
  filteredInProcessingInvoices: InvoiceShort[] = [];
  filteredProcessedInvoices: InvoiceShort[] = [];
  combinedInvoices: InvoiceShort[] = [];
  filteredCombinedInvoices: InvoiceShort[] = [];

  /** The open tab has loaded and has nothing to list: the empty state shows under the table head. */
  get isListEmpty(): boolean {
    return ((!this.filteredInProcessingInvoices?.length && this.isInProcessingTab)
      || (!this.filteredProcessedInvoices?.length && this.isProcessedTab)
      || (!this.filteredCombinedInvoices?.length && this.isCombinedTab)) && !this.loading;
  }

  get hasAnyBill(): boolean {
    const live = (this.inProcessingInvoices?.length || 0) > 0
      || (this.processedInvoices?.length || 0) > 0;
    if (live) localStorage.setItem('synced-had-bill', '1');
    return live || localStorage.getItem('synced-had-bill') === '1';
  }

  isSidebarVisible: boolean = false;
  inProcessingRecord = inProcessingRecord;
  isInProcessingTab: boolean = true;
  isProcessedTab: boolean = false;
  isCombinedTab: boolean = false;
  // Status pill state: at least one is always active. Default = For Review.
  pillForReview: boolean = true;
  pillProcessed: boolean = false;
  isProcessingError: boolean = false;
  isSettingsSidebar: boolean = false;
  isUploadSidebar: boolean = false;
  /** Files dropped anywhere on the page: the upload panel opens with them loaded. */
  droppedFiles: File[] = null;
  /** A file is being dragged over the page, so the drop target is showing. */
  isFileDragActive: boolean = false;
  private fileDragDepth = 0;
  loading: boolean = false;
  columnMode = ColumnMode;
  statuses = InvoiceStatus;
  types = SidebarType;
  isSuperUser: boolean = false;
  user = {} as User;
  processingErrorRecords = [] as ErrorRecord[];
  isFilterShown: boolean = false;
  showToolTipDelay: number = 400;
  filterParams = new BookkeepingFilter();

  processedPage: number = 1;
  processedPageSize: number = 25;
  processedTotalAmount: number = 0;

  // Page size options for user selection
  pageSizeOptions: number[] = [25, 50, 100, 250];

  inProcessingPage: number = 1;
  inProcessingPageSize: number = 1000;
  inProcessingTotalAmount: number = 0;

  // Combined view (both status pills active) - server-side paged.
  combinedPage: number = 1;
  combinedPageSize: number = 25;
  combinedTotalAmount: number = 0;

  // Infinite scroll — when the bottom sentinel scrolls into view the next
  // page is fetched and appended below the existing rows (Processed + Combined
  // views, which would otherwise be paginated).
  loadingMore: boolean = false;
  @ViewChild('infiniteScrollAnchor') infiniteScrollAnchor?: ElementRef<HTMLElement>;
  @ViewChild('inboxPane') inboxPane?: ElementRef<HTMLElement>;
  private infiniteScrollObserver?: IntersectionObserver;

  filterStr: string = '';
  filteredTotalAmount: number = 0;
  sortDirection: string = null;

  matTooltipShowDelay: number = 400;
  tagList = [] as Tag[];
  filteredTagList = [] as Tag[];
  isAddTagMenuShown: boolean = false;
  tagSearchTerm: string = null;
  isAddTagMenuItemShown: boolean = false;
  organizationId: string = null;
  isThreeDotsMenuShown: boolean = false;
  filterDataLoading: boolean = false;
  isExportMenuShown: boolean = false;
  exportOption: number = 1;
  /** SD-1235: the loading skeleton's columns, laid out like the Inbox table's. */
  readonly skeletonColumns: SkeletonColumn[] = INBOX_SKELETON_COLUMNS.filter(c => c.label !== 'Submitter');
  readonly skeletonColumnsWithSubmitter: SkeletonColumn[] = INBOX_SKELETON_COLUMNS;
  /** SD-1235: the selection bar's sum, and its placement over the list pane. */
  selectedInvoicesTotal: number = 0;
  selectionBarLeft: number = 0;
  selectionBarWidth: number = 0;
  isDownloadingSelection: boolean = false;
  isDeletingSelection: boolean = false;

  trackingCategories: TrackingCategory[] = [];
  selectedTrackingCategoriesIds: string[] = [];
  selectedTrackingCategories: TrackingCategory[] = [];
  accounts: PaymentAccount[] = [];
  emptyMessage: string = null;
  selectedSavedFilters: ReportFilter[] = [];
  savedFilters = [] as ReportFilter[];
  isShowSavedFilterList: boolean = false;
  showFilterPanel: boolean = false;
  selectedFilterId: string = "";
  isEditMode: boolean = false;
  isSelectedTab: boolean = false;
  searchCriterias: string[] = [];
  hasActiveUnsavedFilters: boolean = false;
  currentFilterCriteria: string = "";
  defaultFilter: ReportFilter = {
    id: '',
    name: 'All',
    type: 0,
    isPinned: true,
    searchCriteria: '',
    createdBy: '',
    isSelectedTab: true
  };
  screenBrakepoints = ScreenBrakepoints;
  screenBrakepoint: ScreenBrakepoints = ScreenBrakepoints.MinWidth769px;
  showDetailPanel: boolean = false;

  // In-page invoice detail overlay (driven by the `id` query param) — opening a
  // row keeps the filtered table underneath; closing just drops `id`. Edit vs
  // View follows the row's status (For Review = editable, Processed = view).
  overlayInvoiceId: string = null;
  overlayEditMode: boolean = true;
  overlayHasDocument: boolean = true;
  @ViewChild('invoiceDetailTpl') invoiceDetailTpl: TemplateRef<any>;
  private _detailOverlayRef: OverlayRef;
  // ?id=<guid> deep-link captured in ngOnInit; the overlay is actually opened in
  // ngAfterViewInit once the template ref exists.
  private _pendingDeepLinkId: string = null;
  get isOverlayOpen(): boolean { return !!this.overlayInvoiceId; }

  checkedProcessedInvoices: InvoiceShort[] = [];
  checkedInProcessingInvoices: InvoiceShort[] = [];
  checkedCombinedInvoices: InvoiceShort[] = [];
  canPublish: boolean = true;

  invoice: Invoice = new Invoice(
    this._organizationService.selectedOrganization
  );
  creditNote: CreditNoteDTO = new CreditNoteDTO(
    this._organizationService.selectedOrganization
  );
  bankTransation: BankTransactionDTO = new BankTransactionDTO(
    this._organizationService.selectedOrganization
  );
  type: string = '';
  subscriptions: Subscription[] = [];
  // The inbox list fetch (getInboxInvoicesUploadedByUser) is fired on every
  // keystroke. `untilThis` only cancels on component destroy, so without this
  // the in-flight requests pile up and resolve out of order — a slow early
  // response can clobber the latest (e.g. "wo" results landing after "officeworks").
  // We keep one slot and abort the previous request before issuing a new one so
  // only the newest resolves. Infinite-scroll "append" pages are additive and
  // must each complete, so they never cancel a prior request.
  private inboxListSub: Subscription | null = null;
  IsQuickBook: boolean = false;
  IsNoAccounting: boolean = false;
  lineItemList = [];
  bankAccountList: PaymentAccount[] = [];
  private _unsubscribe$ = new Subject<void>();

  @ViewChild(BookkeepingFilterComponent) filterComponent: BookkeepingFilterComponent;
  @ViewChild(InboxFilterComponent) inboxFilterComponent: InboxFilterComponent;
  @ViewChild(PageSidebarComponent) pageSidebarComponent: PageSidebarComponent;
  @ViewChild(TableComponent) tableComponent: TableComponent;

  // Add property to track submitter column visibility
  showSubmitterColumn: boolean = true;
  private readonly baseColumnOptions: ColumnOption[] = [
    { key: 'supplier', label: 'Supplier' },
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'category', label: 'Category' },
    { key: 'tags', label: 'Tags' },
    { key: 'total', label: 'Total' }
  ];
  columnOptions: ColumnOption[] = [];
  columnVisibility: Record<string, boolean> = {};
  private readonly columnVisibilityStorageKey = 'inboxColumnVisibility';

  // Search filter toolbar configuration for inbox
  inboxSearchFilterConfig: SearchFilterConfig = {
    searchPlaceholder: 'Search inbox...',
    showSearch: true,
    showFilter: false, // Status is now selected via the pills, not this dropdown
    enableMultipleFilters: false, // Disable multiple filters
    resultLabel: 'invoices',
    // The match count lives in the Filter Applied badge instead.
    showResultsCount: false,
    filterOptions: [
      { value: 'for-review', label: 'For Review', color: '#ff9800' },
      { value: 'processed', label: 'Processed', color: '#4caf50' }
    ]
  };

  // Search filter toolbar state
  inboxSearchText: string = '';
  selectedInboxStatusFilter: string = 'for-review';
  isFilterExpanded: boolean = false;
  isMobileDevice: boolean = false;

  constructor(
    private _invoicesService: InvoiceService,
    private _organizationService: OrganizationService,
    private _toasterMessageService: ToasterMessageService,
    private _userService: UserService,
    private _errorsService: ErrorsService,
    private _activatedRoute: ActivatedRoute,
    private readonly _location: Location,
    private readonly _searchService: SearchService,
    private readonly _titleService: Title,
    private readonly _invoiceService: InvoiceService,
    private readonly _tagsService: TagsService,
    private readonly _bankAccountService: BankAccountService,
    private readonly _sharedService: SharedService,
    private readonly _filterService: FilterService,
    private _confirmationDialogService: ConfirmationDialogService,
    private _inboxInvoicesStorage: InboxInvoicesStorageService,
    private _recognizedInvoiceService: RecognizedInvoiceService,
    private router: Router,
    private _overlay: Overlay,
    private _vcr: ViewContainerRef,
    private configService: AppConfigService,
    private _zone: NgZone
  ) {
    super();
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };
    this._titleService.setTitle("Synced | Inbox");
    this.updateColumnOptions();
  }

  ngOnInit() {
    this.listenForFileDrops();
    this.checkIfMobileDevice();
    this.savedFilters.push(this.defaultFilter);
    this.selectedSavedFilters.push(this.defaultFilter);
    this.getTrackingCategories();
    this.getQBOClasses();
    this.setIsQuickBookOrNot();
    this.GetBankAcoountDetails();
    this.subscribeToAddingInvoice();
    // SD-1151: an invoice that finished processing without this tab having uploaded it.
    this.subscribeToProcessedInvoices();

    this._sharedService.screenBrakepoint$
      .pipe(this.untilThis)
      .subscribe(
        (breakpoint) => {
          this.screenBrakepoint = breakpoint;
        });
    this.subscriptions.push(this._userService.loggedUserDetails
      .subscribe(data => {
        if (data) {
          this.userDetails = data;
        } else {
          this.userDetails = [];
        }
      }));

    this.organizationId = this._organizationService.selectedOrganization;
    this.applyStatusPills();
    // The detail-overlay `id` param is not a filter, so exclude it from the
    // "are there filters to restore?" check.
    if (this._activatedRoute.snapshot.queryParamMap.keys?.filter(k => k !== 'id').length > 1) {
      setTimeout(() => { this.getSearchState(this.router.url.split('?').length > 0 ? this.router.url.split('?')[1] : '') }, 4000);
    }

    // Deep-link: ?id=<guid> opens the read-only detail overlay for that bill.
    // applyStatusPills() above rewrote the URL without `id`, so re-apply it.
    const deepLinkId = this._activatedRoute.snapshot.queryParamMap.get('id');
    if (deepLinkId) {
      this.overlayInvoiceId = deepLinkId;
      this.overlayEditMode = this._activatedRoute.snapshot.queryParamMap.get('isProcessed') !== 'true';
      this.setOverlayIdInUrl(deepLinkId);
      // The CDK portal needs invoiceDetailTpl (a @ViewChild), which isn't
      // resolved until ngAfterViewInit — defer the actual open to there.
      this._pendingDeepLinkId = deepLinkId;
    }

    this.getProcessingErrors();

    this.user = JSON.parse(this._userService.upUserDetails) as User;
    this.isSuperUser = this.user.role == 1;

    this.getOrganizationTags();
    this.setSearch();
    this.setInboxSearch();
    this.getAllSavedFilters();

    // Load saved submitter column preference
    const savedSubmitterColumnPref = localStorage.getItem('showSubmitterColumn');
    if (savedSubmitterColumnPref !== null) {
      this.showSubmitterColumn = savedSubmitterColumnPref === 'true';
    }
    this.initializeColumnVisibility();
    this._userService
      .getCanPublish(this._organizationService.selectedOrganization, this.user.id)
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.canPublish = response.canPublish;
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  setIsQuickBookOrNot() {
    this.subscriptions.push(
      this._organizationService.organizationList.pipe(this.untilThis).subscribe((data) => {
        const org = data?.find(
          (o) =>
            o.organisationID === this._organizationService.selectedOrganization
        );
        this.IsQuickBook =
          org?.accountingIntegration === AccountingIntegration.QBO
            ? true
            : false;
        this.IsNoAccounting =
          org?.accountingIntegration === AccountingIntegration.None
            ? true
            : false;
      })
    );
  }

  getInProcessingInvoices() {
    this.loading = true;
    // Abort any in-flight inbox fetch so out-of-order responses can't clobber
    // the latest results (see inboxListSub).
    this.inboxListSub?.unsubscribe();
    this.inboxListSub = this._invoicesService.getInboxInvoicesUploadedByUser(
      this._organizationService.selectedOrganization,
      this.inProcessingPage,
      this.inProcessingPageSize,
      'desc',
      false,
      this.inboxSearchText || '') // Use our search filter text
      .pipe(this.untilThis)
      .subscribe(
        (invoices: InboxInvoices) => {
          //this.storedInvoices = invoices.invoices;
          // Normalise a null/missing list to [] so the empty-state placeholder
          // renders and no stale rows linger from a previous org.
          this.inProcessingInvoices = invoices?.invoices || [];
          this.inProcessingTotalAmount = invoices?.totalAmount;
          if (this.inProcessingInvoices) {
            this.prepareProcessedInvoices(this.inProcessingInvoices);

            if (this.isFilterShown) {
              this.filteredInProcessingInvoices = this.filterInProcessingInvoices(this.inProcessingInvoices);
            } else {
              this.filteredInProcessingInvoices = this.inProcessingInvoices;
            }

            // Apply our search filter as well
            this.applyInboxFilters();
          }
          this.loading = false;
          this.setTableEmptyMessage();
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )
  }

  // `append` = infinite-scroll load-more: keep existing rows, add the next page.
  getProcessedInvoices(append: boolean = false) {
    if (append) { this.loadingMore = true; } else { this.loading = true; }
    // Fresh loads (keystroke search, sort, tab change) abort the in-flight inbox
    // fetch so a stale response can't overwrite newer results. Append (load-more)
    // pages are additive, so they don't cancel a prior request.
    if (!append) { this.inboxListSub?.unsubscribe(); }
    this.inboxListSub = this._invoicesService.getInboxInvoicesUploadedByUser(
      this._organizationService.selectedOrganization,
      this.processedPage,
      this.processedPageSize,
      this.sortDirection || 'desc',
      true, this.inboxSearchText)
      .pipe(this.untilThis)
      .subscribe(
        (invoices: InboxInvoices) => {
          const batch = invoices?.invoices || [];
          this.processedTotalAmount = invoices?.totalAmount;
          if (batch.length > 0) { this.prepareProcessedInvoices(batch); }
          this.processedInvoices = append
            ? [...(this.processedInvoices || []), ...batch]
            : batch;
          this.filteredProcessedInvoices = [...this.processedInvoices];
          // An empty load-more page means there is nothing left — stop here.
          if (append && batch.length === 0) { this.processedTotalAmount = this.processedInvoices.length; }
          this.loading = false;
          this.loadingMore = false;
        },
        (e: Error) => {
          this.loading = false;
          this.loadingMore = false;
          // Roll the page back so the next scroll retries the same page.
          if (append) { this.processedPage = Math.max(1, this.processedPage - 1); }
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )
  }

  // New method for getting processed invoices for user through our search filter
  getProcessedInvoicesForUser() {
    this.loading = true;
    // Abort any in-flight inbox fetch so out-of-order responses can't clobber
    // the latest results (see inboxListSub).
    this.inboxListSub?.unsubscribe();
    this.inboxListSub = this._invoicesService.getInboxInvoicesUploadedByUser(
      this._organizationService.selectedOrganization,
      this.processedPage,
      this.processedPageSize,
      this.sortDirection || 'desc',
      true,
      this.inboxSearchText || '') // Use our search filter text
      .pipe(this.untilThis)
      .subscribe(
        (invoices: InboxInvoices) => {
          this.processedInvoices = invoices?.invoices;
          this.processedTotalAmount = invoices?.totalAmount;

          if (this.processedInvoices && this.processedInvoices?.length > 0) {
            this.prepareProcessedInvoices(this.processedInvoices);
            this.filteredProcessedInvoices = [...this.processedInvoices];
          } else {
            this.filteredProcessedInvoices = [];
          }

          // Apply our search filter
          this.applyInboxFilters();
          this.loading = false;
        },
        (e: Error) => {
          this.loading = false;
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )
  }

  // Returns the currently visible (filtered) invoice list for whichever
  // status mode is active, so bulk actions target the right rows.
  get activeFilteredInvoices(): InvoiceShort[] {
    if (this.isCombinedTab) { return this.filteredCombinedInvoices || []; }
    if (this.isProcessedTab) { return this.filteredProcessedInvoices || []; }
    return this.filteredInProcessingInvoices || [];
  }

  toggleStatusPill(pill: 'forReview' | 'processed'): void {
    // The toolbar pills are mutually exclusive — selecting one deselects the
    // other. The combined "all statuses" view is reached via the filter
    // panel's Status dropdown, not the pills.
    if (pill === 'forReview') {
      if (this.pillForReview && !this.pillProcessed) { return; } // already sole
      this.pillForReview = true;
      this.pillProcessed = false;
    } else {
      if (this.pillProcessed && !this.pillForReview) { return; } // already sole
      this.pillProcessed = true;
      this.pillForReview = false;
    }
    this.combinedPage = 1;
    this.processedPage = 1;
    this.applyStatusPills();
  }

  // Loads the right invoice list for the active pill combination.
  private applyStatusPills(): void {
    this.loading = true;
    this.isProcessingError = false;

    if (this.pillForReview && this.pillProcessed) {
      this.isInProcessingTab = false;
      this.isProcessedTab = false;
      this.isCombinedTab = true;
      this._location.replaceState('/inbox');
    } else if (this.pillProcessed) {
      this.isInProcessingTab = false;
      this.isProcessedTab = true;
      this.isCombinedTab = false;
      this.selectedInboxStatusFilter = 'processed';
      this._location.replaceState('/inbox?isProcessed=true');
    } else {
      this.pillForReview = true;
      this.isInProcessingTab = true;
      this.isProcessedTab = false;
      this.isCombinedTab = false;
      this.selectedInboxStatusFilter = 'for-review';
      this._location.replaceState('/inbox?isProcessed=false');
    }

    // Keep an active filter applied across status changes — the search
    // endpoint handles every status, so the filter just carries over.
    if (this.isFilterShown && this.currentFilterCriteria) {
      this.getSearchState(this.currentFilterCriteria);
    } else if (this.isCombinedTab) {
      this.getCombinedInvoices();
    } else if (this.isProcessedTab) {
      this.getProcessedInvoicesForUser();
    } else {
      this.getInProcessingInvoices();
    }
  }

  // --- Invoice detail overlay -------------------------------------------------

  /**
   * Open the in-page invoice detail for the clicked row. The detail is the same
   * component as the editInvoice page (read-only here); we add the `id` query
   * param to the live URL so the filtered table is preserved underneath.
   */
  openInvoiceOverlay(e: { id: string; editMode: boolean }): void {
    if (!e?.id) { return; }
    this.overlayInvoiceId = e.id;
    this.overlayEditMode = e.editMode;
    this.setOverlayIdInUrl(e.id);

    // Render the detail through a CDK overlay: it attaches to a body-level
    // container (immune to any ancestor transform that breaks position:fixed)
    // and its block-scroll strategy freezes the page at its exact visual
    // position, so the table keeps its spot behind the modal.
    if (!this._detailOverlayRef) {
      this._detailOverlayRef = this._overlay.create({
        positionStrategy: this._overlay.position().global(),
        scrollStrategy: this._overlay.scrollStrategies.block(),
        hasBackdrop: true,
        backdropClass: 'inbox-detail-backdrop',
        panelClass: 'inbox-detail-pane',
        // Don't size the pane to the full viewport: the panel inside is
        // position:fixed, so a full-size pane only serves to blanket the
        // backdrop and swallow the outside click (making click-away-to-close
        // dead). Let the pane collapse to the fixed panel so the backdrop stays
        // clickable.
      });
      this._detailOverlayRef.backdropClick().subscribe(() => this.closeInvoiceOverlay());
      // Esc closes the overlay — a reliable path that doesn't depend on the
      // in-panel close button being hittable.
      this._detailOverlayRef.keydownEvents().subscribe((e: KeyboardEvent) => {
        if (e.key === 'Escape') { this.closeInvoiceOverlay(); }
      });
    }
    if (!this._detailOverlayRef.hasAttached()) {
      this._detailOverlayRef.attach(new TemplatePortal(this.invoiceDetailTpl, this._vcr));
    }
  }

  /** Close the overlay: drop only `id`, every filter param stays. */
  closeInvoiceOverlay(): void {
    this.overlayInvoiceId = null;
    this.overlayHasDocument = true;
    this.setOverlayIdInUrl(null);
    if (this._detailOverlayRef) {
      this._detailOverlayRef.dispose();
      this._detailOverlayRef = null;
    }
  }

  /**
   * Add/remove the `id` query param on the live browser URL while leaving the
   * filter params byte-for-byte (so we don't fight the inbox's manual
   * replaceState URL handling).
   */
  private setOverlayIdInUrl(id: string | null): void {
    let path = this.removeIdParam(this._location.path());
    if (id) {
      path += (path.includes('?') ? '&' : '?') + `id=${id}`;
    }
    this._location.replaceState(path);
  }

  private removeIdParam(path: string): string {
    return path
      .replace(/([?&])id=[^&]*/g, '$1')
      .replace(/\?&/g, '?')
      .replace(/&&/g, '&')
      .replace(/[?&]$/g, '');
  }

  // Combined (both pills) view: one merged list. `append` = infinite-scroll
  // load-more (keep existing rows, add the next page).
  getCombinedInvoices(append: boolean = false): void {
    if (append) { this.loadingMore = true; } else { this.loading = true; }
    this._invoicesService.getInboxInvoicesUploadedByUser(
      this._organizationService.selectedOrganization,
      this.combinedPage,
      this.combinedPageSize,
      this.sortDirection || 'desc',
      null,
      this.inboxSearchText || '')
      .pipe(this.untilThis)
      .subscribe(
        (invoices: InboxInvoices) => {
          const batch = invoices?.invoices || [];
          this.combinedTotalAmount = invoices?.totalAmount || 0;
          if (batch.length > 0) { this.prepareProcessedInvoices(batch); }
          this.combinedInvoices = append
            ? [...(this.combinedInvoices || []), ...batch]
            : batch;
          this.filteredCombinedInvoices = [...this.combinedInvoices];
          // An empty load-more page means there is nothing left — stop here.
          if (append && batch.length === 0) { this.combinedTotalAmount = this.combinedInvoices.length; }
          this.loading = false;
          this.loadingMore = false;
          this.setTableEmptyMessage();
        },
        (e: Error) => {
          this.loading = false;
          this.loadingMore = false;
          // Roll the page back so the next scroll retries the same page.
          if (append) { this.combinedPage = Math.max(1, this.combinedPage - 1); }
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  combinedPageSelect(page: number): void {
    this.combinedPage = page;
    this.getCombinedInvoices();
  }

  combinedPageSizeSelect(pageSize: number): void {
    this.combinedPageSize = pageSize;
    this.combinedPage = 1;
    this.getCombinedInvoices();
  }

  combinedSortTable(sortData: SortTableParameters): void {
    if (sortData.columnName != 'Supplier') { return; }
    if (this.sortDirection == null || this.sortDirection == 'asc') {
      this.sortDirection = 'desc';
    } else {
      this.sortDirection = 'asc';
    }
    this.combinedPage = 1;
    this.getCombinedInvoices();
  }

  private applyFiltersToCombined(): void {
    this.combinedPage = 1;
    this.getCombinedInvoices();
  }

  createNewTag() {
    this.isAddTagMenuItemShown = false;

    let tag = {
      backColor: null,
      textColor: null,
      tagName: this.tagSearchTerm
    } as Tag;

    tag.organizationId = this.organizationId;

    this._tagsService.createTag(tag)
      .pipe(take(1))
      .subscribe(
        (createdTag: Tag) => {
          tag = { ...createdTag };
          this.tagList = [...this.tagList, tag];
          this.filteredTagList = [...this.tagList];
          this.addTagToInvoices(tag, false);
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  addTagToInvoices(tag: Tag, showWarning: boolean = false) {
    let checkedInvoices = this.activeFilteredInvoices.filter(i => i.checked && !i.tags?.some(t => t.tagId == tag.tagId));

    if (checkedInvoices?.length == 0) {
      if (showWarning) {
        this._toasterMessageService.displayToasterState(ToasterTypes.Warning, 'Warning', 'Please, select invoices first!');
      }
      return;
    }

    let invoiceIds = [] as string[];
    checkedInvoices.forEach(invoice => {
      if (!invoice.tags?.some(t => t.tagId == tag.tagId)) {
        invoiceIds.push(invoice.id.toString());
      }
    });

    let tagToInvoices = {
      invoiceIds: invoiceIds,
      tagId: tag.tagId
    } as TagToInvoices;

    this._tagsService.addTagToInvoices(tagToInvoices)
      .pipe(take(1))
      .subscribe(
        () => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', 'Tag added to selected invoices!');
          checkedInvoices.forEach(invoice => {
            if (invoice.tags) {
              invoice.tags = [...invoice.tags, tag];
            } else {
              invoice.tags = [tag];
            }
          });

          if (this.isCombinedTab) {
            this.filteredCombinedInvoices = [...this.filteredCombinedInvoices];
          } else if (this.isInProcessingTab) {
            this.filteredInProcessingInvoices = [...this.filteredInProcessingInvoices];
          } else {
            this.filteredProcessedInvoices = [...this.filteredProcessedInvoices];
            this.loading = false;
          }

        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  deleteTag(deletedTag: DeletedTagFromInvoice) {
    this._tagsService.deleteTagFromInvoices(deletedTag.tagId, deletedTag.invoiceId)
      .pipe(this.untilThis)
      .subscribe(
        () => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', `Tag removed from the invoice!`);

          if (this.isCombinedTab) {
            let updatedInvoice = this.filteredCombinedInvoices.find(i => i.id.toString().toLowerCase() == deletedTag.invoiceId.toLowerCase());
            if (updatedInvoice) {
              updatedInvoice.tags = updatedInvoice.tags?.filter(t => t.tagId != deletedTag.tagId);
            }
            this.filteredCombinedInvoices = [...this.filteredCombinedInvoices];
          } else if (deletedTag.invoiceProcessedStatus) {
            let updatedInvoice = this.filteredProcessedInvoices.find(i => i.id.toString().toLowerCase() == deletedTag.invoiceId.toLowerCase());
            updatedInvoice.tags = updatedInvoice?.tags.filter(t => t.tagId != deletedTag.tagId);
            this.filteredProcessedInvoices = [...this.filteredProcessedInvoices];
          } else {
            let updatedInvoice = this.filteredInProcessingInvoices.find(i => i.id.toString().toLowerCase() == deletedTag.invoiceId.toLowerCase());
            updatedInvoice.tags = updatedInvoice?.tags.filter(t => t.tagId != deletedTag.tagId);
            this.filteredInProcessingInvoices = [...this.filteredInProcessingInvoices];
          }
          this.loading = false;
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  filterTags(event) {
    this.tagSearchTerm = event;
    this.filteredTagList = this.tagList.filter(t => t.tagName.toLowerCase().includes(this.tagSearchTerm?.toLowerCase()));
    let exactlyMatchTags = this.filteredTagList.filter(t => t.tagName.toLowerCase() == this.tagSearchTerm?.toLowerCase());

    if (exactlyMatchTags?.length == 0) {
      this.isAddTagMenuItemShown = true;
    } else {
      this.isAddTagMenuItemShown = false;
    }
  }

  clearTagSearchValue() {
    setTimeout(() => {
      this.tagSearchTerm = null;
      this.filteredTagList = this.tagList;
    }, 250);
  }

  showAddTagMenu() {
    this.isAddTagMenuShown = true;
  }

  menuClosed() {
    setTimeout(() => {
      this.isAddTagMenuShown = false;
      this.isExportMenuShown = false;
    }, 100);
  }

  rowCheckboxClickEvent() {
    this.checkedInProcessingInvoices = (this.filteredInProcessingInvoices || []).filter(i => i.checked);
    this.checkedProcessedInvoices = (this.filteredProcessedInvoices || []).filter(i => i.checked);
    this.checkedCombinedInvoices = (this.filteredCombinedInvoices || []).filter(i => i.checked);

    const wasShown = this.isThreeDotsMenuShown;
    this.isThreeDotsMenuShown = this.checkedInProcessingInvoices.length > 0 || this.checkedProcessedInvoices.length > 0
      || this.checkedCombinedInvoices.length > 0;

    // SD-1235: summed once per tick here rather than in a template getter on every change detection.
    this.selectedInvoicesTotal = this.activeFilteredInvoices
      .filter(i => i.checked)
      .reduce((sum, i) => sum + this.invoiceAmount(i), 0);

    if (this.isThreeDotsMenuShown && !wasShown) this.refreshSelectionBar();
  }

  /** The figure the Total column shows for a row. */
  private invoiceAmount(invoice: InvoiceShort): number {
    const raw: any = (invoice as any).amountDue === 'Infinity' ? (invoice as any).total : invoice.amountDue;
    const amount = Number(raw);
    return isFinite(amount) ? amount : 0;
  }

  /** SD-1235: the bar's "Clear" — unticks every row of the list on screen. */
  clearSelection(): void {
    this.activeFilteredInvoices.forEach(i => i.checked = false);
    if (this.isCombinedTab) {
      this.filteredCombinedInvoices = [...this.filteredCombinedInvoices];
    } else if (this.isProcessedTab) {
      this.filteredProcessedInvoices = [...this.filteredProcessedInvoices];
    } else {
      this.filteredInProcessingInvoices = [...this.filteredInProcessingInvoices];
    }
    this.rowCheckboxClickEvent();
  }

  /** SD-1235: the selection bar is fixed to the viewport (the list scrolls under it), so it is
   *  placed against the list pane's measured edges — the sidebar's width differs by breakpoint
   *  and when it collapses. */
  refreshSelectionBar(): void {
    requestAnimationFrame(() => {
      const pane = this.inboxPane?.nativeElement as HTMLElement;
      if (!pane) return;
      const rect = pane.getBoundingClientRect();
      this.selectionBarLeft = rect.left;
      this.selectionBarWidth = rect.width;
    });
  }

  @HostListener('window:resize')
  onSelectionBarResize(): void {
    if (this.isThreeDotsMenuShown) this.refreshSelectionBar();
  }

  trackTagById(_index: number, tag: Tag): any {
    return tag?.tagId;
  }

  // Count shown in the bulk-action bar. The active list is one of three views
  // (in-processing / processed / the combined view shown when a filter spans
  // both statuses), each with its own checked array. The combined case is the
  // one that read as "0 Selected" after "select all" under an applied filter,
  // because the count only looked at the in-processing/processed arrays.
  get selectedInvoicesCount(): number {
    if (this.isInProcessingTab) return this.checkedInProcessingInvoices?.length || 0;
    if (this.isProcessedTab) return this.checkedProcessedInvoices?.length || 0;
    if (this.isCombinedTab) return this.checkedCombinedInvoices?.length || 0;
    return 0;
  }

  toggleSidebar(sidebar: SidebarType) {
    this.droppedFiles = null;
    if (sidebar === SidebarType.settings)
      this.isSettingsSidebar = !this.isSettingsSidebar;
    else if (sidebar === SidebarType.upload)
      this.isUploadSidebar = !this.isUploadSidebar;

    this.isSidebarVisible = !this.isSidebarVisible;
  }

  downloadCheckedInvoicesAsZip(checkedInvoices: InvoiceShort[]) {
    let invoiceKeys = [] as InvoiceKey[];

    checkedInvoices.forEach(element => {
      if (element.invoicePdfUrl) {
        invoiceKeys.push(
          {
            invoicePdfUrlId: element.invoicePdfUrl,
            invoiceName: `${element.supplierName ?? ''} ${(element.invoiceNumber ?? '').replace(/\//g, '')} ${element.accountName ?? ''}`.trim()
          } as InvoiceKey
        );
      }
    });

    // SD-1235: nothing to archive — say so and stop, rather than requesting an empty zip.
    if (invoiceKeys.length == 0) {
      this._toasterMessageService.displayToasterState(ToasterTypes.Warning, 'Warning', 'Selected invoices have no attachments');
      return;
    }

    let request = {
      organizationId: this.organizationId,
      invoices: invoiceKeys
    } as ArchiveRequest;

    this._toasterMessageService.displayToasterState(ToasterTypes.Info, 'Info', 'Archive is being prepared for download. Please, wait a final message');

    this.isDownloadingSelection = true;
    this._invoiceService.downloadZipArchive(request)
      .pipe(this.untilThis, finalize(() => this.isDownloadingSelection = false))
      .subscribe(
        (archive: Blob) => {
          let date = new Date();
          const blob = new Blob([archive], { type: 'application/zip' });
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', 'Archive ready for download!')
          saveAs(blob, `invoices ${date.toLocaleDateString()}.zip`);
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message)
        }
      );
  }

  downloadCheckedInvoices() {
    let checkedInvoices = this.activeFilteredInvoices.filter(inv => inv.checked);

    if (checkedInvoices?.length > 1) {
      this.downloadCheckedInvoicesAsZip(checkedInvoices);
      return;
    }

    const invoice = checkedInvoices[0];
    if (!invoice) return;
    if (!invoice.invoicePdfUrl) {
      this._toasterMessageService.displayToasterState(ToasterTypes.Warning, 'Warning', 'This document has no attachment');
      return;
    }

    this.isDownloadingSelection = true;
    this._invoiceService.downloadFile(invoice.invoicePdfUrl, '')
      .pipe(take(1), finalize(() => this.isDownloadingSelection = false))
      .subscribe(
        (file: Blob) => {
          const blob = new Blob([file], { type: 'application/pdf' });
          saveAs(blob, `${invoice.supplierName ?? 'Document'} ${invoice.invoiceNumber ?? ''}`.trim() + '.pdf');
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message)
        }
      );
  }

  changeTab(tab: string) {
    // this.toggleFilterSearch(false);
    this.resetSearch();
    this.storedInvoices = [];
    if (tab === 'InProcessing') {

      this.isInProcessingTab = true
      this.isProcessedTab = false;
      this.isProcessingError = false;
      // Load fresh in-processing data
      this.getInProcessingInvoices();
      // Update filter to reflect current tab
      this.selectedInboxStatusFilter = 'for-review';
    }
    else if (tab === 'Processed') {
      this.isInProcessingTab = false;
      this.isProcessedTab = true;
      this.isProcessingError = false;
      // Load fresh processed data
      this.getProcessedInvoices();
      // Update filter to reflect current tab
      this.selectedInboxStatusFilter = 'processed';
    }
    else if (tab === 'ProcessingError') {
      this.isInProcessingTab = false;
      this.isProcessedTab = false;
      this.isProcessingError = true;
    }
  }

  toggleFilterSearch(event: ReportFilter) {
    this.loading = true;
    this.getSearchState(event.searchCriteria);
    this.savedFilters.filter(x => x.id == event.id);
    let indexToUpdate = this.savedFilters.findIndex(item => item.id === event.id);
    if (indexToUpdate > -1) {
      this.savedFilters[indexToUpdate] = event;
    }
    else {
      this.savedFilters.push(event);
    }
    this.selectedSavedFilters.forEach(x => x.isSelectedTab = false);
    if (!this.isEditMode) {
      event.isSelectedTab = true;
      this.selectedSavedFilters.push(event);
    }
    else {
      indexToUpdate = this.selectedSavedFilters.findIndex(x => x.id == event.id);
      event.isSelectedTab = true;
      this.selectedSavedFilters[indexToUpdate] = event;
    }
    this.isEditMode = false;

    // Update filter application state
    this.updateFilterApplication(event.searchCriteria, true);

    this.toggleFilterSidebar();
    this.filterInvoices(event.searchCriteria);
  }

  sortTable(sortData: SortTableParameters) {
    if (sortData.columnName != 'Supplier') {
      return;
    }

    this.filteredProcessedInvoices = null;
    this.loading = true;
    this.processedPage = 1;

    if (this.sortDirection == null || this.sortDirection == 'asc') {
      this.sortDirection = 'desc';
    } else {
      this.sortDirection = 'asc';
    }

    if (this.isFilterShown) {
      let filterStr = `${this.filterStr}&page=${1}&pageSize=${this.processedPageSize}&sort=${this.sortDirection}`;
      this._location.replaceState(`/inbox?isProcessed=${this.selectedInboxStatusFilter === 'for-review' ? false : true}&${filterStr}&filterId=${this.selectedFilterId}`);
      this._searchTerms.next(`${filterStr}&organizationId=${this._organizationService.selectedOrganization}`);
    } else {
      this.getProcessedInvoices();
    }
  }

  filterInvoices(filterStr: string) {
    this.processedPage = 1;
    this.isFilterShown = true;
    if (this.sortDirection == null)
      this.sortDirection = "asc";
    if (this.isInProcessingTab) {
      this.filteredInProcessingInvoices = [...this.filterInProcessingInvoices(this.inProcessingInvoices)];
      this.setTableEmptyMessage();
      this.loading = false;
    }

    if (this.isProcessedTab) {
      this.filterStr = filterStr;
      if (filterStr != "") {
        filterStr = `${filterStr}&page=${1}&pageSize=${this.processedPageSize}&sort=${this.sortDirection}`;
        filterStr = filterStr.replace("status=false", "status=true");
      }
      else {
        filterStr = `?page=${1}&pageSize=${this.processedPageSize}&sort=${this.sortDirection}`;
      }
      this._location.replaceState(`/inbox?isProcessed=${this.selectedInboxStatusFilter === 'for-review' ? false : true}&${filterStr}&filterId=${this.selectedFilterId}`);
      this._searchTerms.next(`${filterStr}&organizationId=${this._organizationService.selectedOrganization}`);
      // this.loading = false;
    }
  }

  pageSelect(page: number) {
    this.processedPage = page;
    if (this.isFilterShown) {
      let queryParamsStr = `${this.filterStr}&page=${this.processedPage}&pageSize=${this.processedPageSize}&sort=${this.sortDirection}`;
      this._location.replaceState(`/inbox?isProcessed=${this.selectedInboxStatusFilter === 'for-review' ? false : true}&${queryParamsStr}&filterId=${this.selectedFilterId}`);
      this._searchTerms.next(`${queryParamsStr}&organizationId=${this._organizationService.selectedOrganization}`);
    } else {
      this.getProcessedInvoices();
    }
  }

  pageSizeSelect(pageSize: number) {
    this.processedPageSize = pageSize;
    this.processedPage = 1; // Reset to first page when changing page size
    if (this.isFilterShown) {
      let queryParamsStr = `${this.filterStr}&page=${this.processedPage}&pageSize=${this.processedPageSize}&sort=${this.sortDirection}`;
      this._location.replaceState(`/inbox?isProcessed=${this.selectedInboxStatusFilter === 'for-review' ? false : true}&${queryParamsStr}&filterId=${this.selectedFilterId}`);
      this._searchTerms.next(`${queryParamsStr}&organizationId=${this._organizationService.selectedOrganization}`);
    } else {
      this.getProcessedInvoices();
    }
  }

  private setTableEmptyMessage() {
    this.emptyMessage = this.searchText ? 'No records for that search' : this.isFilterShown ? "The filter values selected show no records" : 'You\'re all up to date. No documents to process 👍';
  }

  private getTrackingCategories() {
    const trackingCategories$ = this._organizationService.getOrganizationTrackingCategoriesWithOptions(this._organizationService.selectedOrganization);
    const selectedTrackingCategoriesIds$ = this._organizationService.getOrganizationTrackingCategories(this._organizationService.selectedOrganization);
    const accounts$ = this._bankAccountService.getPaymentAccounts(this._organizationService.selectedOrganization);

    const forkJoin$ = forkJoin({
      trackingCategories: trackingCategories$,
      selectedTrackingCategoriesIds: selectedTrackingCategoriesIds$,
      accounts: accounts$,
    });

    forkJoin$.subscribe(
      (data) => {
        this.trackingCategories = data.trackingCategories;
        this.selectedTrackingCategoriesIds = data.selectedTrackingCategoriesIds;
        this.selectedTrackingCategories = this.trackingCategories.filter(category => this.selectedTrackingCategoriesIds.some(id => category.id == id));
        this.accounts = data.accounts;//.sort((a, b) => a.name.localeCompare(b.name)) || [];
        this.updateColumnOptions();
      }
    );
  }

  private getQBOClasses() {
    const orgId = this._organizationService.selectedOrganization;
    if (!orgId) { return; }
    this._organizationService.getOrganizationQBOClasses(orgId).subscribe(
      (classes) => {
        this._organizationService.setQBOClasses(classes || []);
      },
      () => {
        this._organizationService.setQBOClasses([]);
      }
    );
  }

  private setSearch() {
    this._searchTerms.pipe(
      this.untilThis,
      debounceTime(500),
      switchMap((filterStr: string) => {
        this.filterDataLoading = true;
        return this._searchService.searchInvoices(filterStr);
      })
    ).subscribe({
      next: (data: InboxInvoices) => {
        let list = data.invoices;
        if (this.filterParams.tags && this.filterParams.tags?.length > 0 && this.filterParams.tags[0] != '') {
          if (this.filterParams.tags.includes('Unassigned tags')) {
            list = this.filteredProcessedInvoices.filter(i => !i.tags || i.tags?.length == 0);
          } else {
            list = this.filteredProcessedInvoices.filter(i => this.filterParams.tags.some(t => i.tags && i.tags.length > 0 ? i.tags.map(p => p.tagName).includes(t) : null));
          }
        }

        if (this.filterParams.trackingOptions1 && this.filterParams.trackingOptions1.length > 0 && this.filterParams.trackingOptions1[0] != '') {
          if (this.filterParams.trackingOptions1.includes('Unassigned tracking')) {
            list = list.filter(i => i.invoiceLines.length > 0 ? i.invoiceLines.some(line => line.trackingOptions?.length == 0) : true);
          } else {
            list = list.filter(i => i.invoiceLines.some(line => line.trackingOptions?.length > 0 ? line.trackingOptions.some(option => this.filterParams.trackingOptions1.includes(option)) : false));
          }
        }
        if (this.filterParams.submitters && this.filterParams.submitters.length > 0 && this.filterParams.submitters[0] != '') {
          list = list.filter(i => this.filterParams.submitters.includes(i.submitterName));
        }

        this.filteredProcessedInvoices = list;
        this.prepareProcessedInvoices(this.filteredProcessedInvoices);

        if (this.filterParams.categories?.length > 0 && this.filterParams.categories[0] != '') {
          this.filteredProcessedInvoices.forEach(item => {
            if (item.invoiceLines?.length > 0) {
              item.invoiceLines = item.invoiceLines?.filter(line => this.filterParams.categories.includes(line.accountName.replace('&', '%26')));
            }
          });
        }

        this.filteredTotalAmount = data.totalAmount;
        this.isFilterShown = true;
        this.loading = false;
        this.filterDataLoading = false;
        this.setTableEmptyMessage();
      },
      error: (e: Error) => {
        this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        this.loading = false;
        this.filterDataLoading = false;
      }
    });
  }

  private prepareProcessedInvoices(array: InvoiceShort[]) {
    let count = 1;
    array.forEach(element => {
      let stringToSplit = element.supplierName;

      if (stringToSplit) {
        let x = stringToSplit.split(" ");
        if (x.length == 1) {
          element.shortCode = x[0].charAt(0);
        }
        if (x.length == 2) {
          element.shortCode = x[0].charAt(0) + x[1].charAt(0);
        }
        if (x.length > 2) {
          element.shortCode = x[0].charAt(0) + x[1].charAt(0) + x[2].charAt(0);
        } if (x.length == 0) {
          element.shortCode = "NA";
        }
      }

      element.rowCount = count;
      count = count + 1;
      if (count == 7) {
        count = 1;
      }

      // Set placeholder for submitterName if not available
      if (!element.submitterName) {
        element.submitterName = 'Submitter Name';
      }

      element.invoiceLines?.forEach(line => {
        line.accountName = this.accounts.find(a => a.id == line.accountId)?.name;

        if (line.trackingOptions && line.trackingOptions.length == 1) {
          line.trackingOptions = this.selectedTrackingCategories.length > 0 ? [this.selectedTrackingCategories[0].options.find(c => c.id == line.trackingOptions[0])?.name] : [];
        } else if (line.trackingOptions && line.trackingOptions.length == 2) {
          const category = this.selectedTrackingCategories.find(c => c.options.some(o => o.id === line.trackingOptions[0]));
          const index = this.selectedTrackingCategories.indexOf(category);

          line.trackingOptionNames = ['', ''];

          if (index == 0) {
            line.trackingOptionNames[0] = this.selectedTrackingCategories[0]?.options?.find(o => o.id == line.trackingOptions[0])?.name;
            line.trackingOptionNames[1] = this.selectedTrackingCategories[1]?.options?.find(o => o.id == line.trackingOptions[1])?.name;
          } else {
            const options = [...line.trackingOptions];
            line.trackingOptionNames[0] = this.selectedTrackingCategories[0]?.options?.find(o => o.id == line.trackingOptions[1])?.name;
            line.trackingOptionNames[1] = this.selectedTrackingCategories[1]?.options?.find(o => o.id == line.trackingOptions[0])?.name;

            line.trackingOptions[0] = options[1];
            line.trackingOptions[1] = options[0];
          }
        }
      });
    });
  }

  private filterInProcessingInvoices(invoices: InvoiceShort[]) {
    let list = [] as InvoiceShort[];
    invoices = JSON.parse(JSON.stringify(invoices));

    if (this.filterParams.suppliers && this.filterParams.suppliers?.length > 0) {
      list = invoices.filter(i => this.filterParams.suppliers?.some(s => i?.supplierName?.includes(s)));
    } else {
      list = invoices;
    }

    // Apply Type filter for in-processing tab
    if (this.filterParams.types && this.filterParams.types.length > 0) {
      list = list.filter(i => this.filterParams.types.includes(i.type));
    }

    const rangeStart = this.filterParams.invoiceDateRange?.[0];
    const rangeEnd = this.filterParams.invoiceDateRange?.[1];
    const rangeStartMs = rangeStart ? new Date(rangeStart.getTime()).setHours(0, 0, 0, 0) : null;
    const rangeEndMs = rangeEnd ? new Date(rangeEnd.getTime()).setHours(0, 0, 0, 0) : null;
    list = list.filter(i => {
      const itemMs = new Date(i?.date).getTime();
      return (rangeStartMs == null || itemMs >= rangeStartMs)
        && (rangeEndMs == null || itemMs <= rangeEndMs);
    });

    if (this.filterParams.categories && this.filterParams.categories?.length > 0 && this.filterParams.categories[0] != '') {
      list = list.filter(i => this.filterParams.categories.some(c => i.invoiceLines?.length > 0 ? i.invoiceLines?.some(line => this.filterParams.categories.map(x => x.replace(/%20/g, ' ').replace(/%26/g, '&')).includes(line.accountName)) : false));
    }

    if (this.filterParams.tags && this.filterParams.tags?.length > 0 && this.filterParams.tags[0] != '') {
      if (this.filterParams.tags.includes('Unassigned tags')) {
        list = list.filter(i => !i.tags || i.tags?.length == 0);
      } else {
        list = list.filter(i => this.filterParams.tags.some(t => i.tags && i.tags.length > 0 ? i.tags.map(p => p.tagName.replace(/%20/g, ' ').replace(/%26/g, '&')).includes(t) : null));
      }
    }

    if (this.filterParams.trackingOptions1 && this.filterParams.trackingOptions1.length > 0 && this.filterParams.trackingOptions1[0] != '') {
      if (this.filterParams.trackingOptions1.includes('Unassigned tracking')) {
        list = list.filter(i => i.invoiceLines.length > 0 ? i.invoiceLines.some(line => line.trackingOptions?.length == 0) : true);
      } else {
        list = list.filter(i => i.invoiceLines.some(line => line.trackingOptions?.length > 0 ? line.trackingOptions.some(option => this.filterParams.trackingOptions1.map(x => x.replace(/%20/g, ' ').replace(/%26/g, '&')).includes(option)) : false));
      }
    }

    if (this.filterParams.trackingOptions2 && this.filterParams.trackingOptions2.length > 0 && this.filterParams.trackingOptions2[0] != '') {
      list = list.filter(i => i.invoiceLines.some(line => line.trackingOptions?.length > 0 ? line.trackingOptions.some(option => this.filterParams.trackingOptions2.map(x => x.replace(/%20/g, ' ').replace(/%26/g, '&')).includes(option)) : false));
    }

    if (this.filterParams.submitters && this.filterParams.submitters?.length > 0 && this.filterParams.submitters[0] != '') {
      list = list.filter(i => this.filterParams.submitters?.some(s => i?.submitterName?.includes(s.replace(/%20/g, ' '))));
    }

    if (this.filterParams.assignedTo && this.filterParams.assignedTo?.length > 0 && this.filterParams.assignedTo[0] != '') {
      list = list.filter(i => this.filterParams.assignedTo?.some(s => i?.assignedTo?.includes(s.replace(/%20/g, ' '))));
    }
    // if (this.filterParams.categories?.length > 0 && this.filterParams.categories[0] != '') {
    //   list.forEach(item => {
    //     if (item.invoiceLines?.length > 0) {
    //       item.invoiceLines = item.invoiceLines?.filter(line => this.filterParams.categories.includes(line.accountName));
    //     }
    //   });
    // } else if (this.filterParams.trackingOptions1?.length > 0 && this.filterParams.trackingOptions1[0] != '') {
    //   list.forEach(item => {
    //     if (item.invoiceLines?.length > 0) {
    //       item.invoiceLines = item.invoiceLines?.filter(line => line.trackingOptions?.some(name => this.filterParams.trackingOptions1.includes(name)));
    //     }
    //   });
    // } else if (this.filterParams.trackingOptions2?.length > 0 && this.filterParams.trackingOptions2[0] != '') {
    //   list.forEach(item => {
    //     if (item.invoiceLines?.length > 0) {
    //       item.invoiceLines = item.invoiceLines?.filter(line => line.trackingOptions?.some(name => this.filterParams.trackingOptions2.includes(name)));
    //     }
    //   });
    // }

    return list;
  }

  private getProcessingErrors() {
    this._errorsService.getErrorRecordsByOrganizationId(this._organizationService.selectedOrganization)
      .pipe(this.untilThis)
      .subscribe(
        (recordsData: ErrorRecord[]) => {
          this.processingErrorRecords = recordsData.filter(rd => rd.errorRecordType == ErrorRecordType.Email);
        },
        (e: Error) => {
          this.loading = false;
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message)
        }
      )
  }

  private getSearchState(filterCriteria: string) {
    this.loading = true;
    let dateStart: Date;
    let dateEnd: Date;
    let dateOption: string = "";
    // `cond=` / `desc=` are the filter panel's own record of what was entered;
    // the flat parameters below are what the search runs on.
    filterCriteria = stripPanelParams(filterCriteria);
    if (filterCriteria != undefined && filterCriteria != '') {
      this.searchCriterias = filterCriteria.replace("?", "").split("&");

      dateOption = this.searchCriterias.filter(x => x.includes('dateOption'))[0]?.split('=')[1];
      let dateStartStr = this.searchCriterias.filter(x => x.includes('dateStart'))[0]?.split('=')[1];
      let dateEndStr = this.searchCriterias.filter(x => x.includes('dateEnd'))[0]?.split('=')[1];

      if (dateOption == "0") {
        dateStartStr = dateToString(new Date());
        dateEndStr = dateToString(new Date());
      }
      else if (dateOption == "1") {
        var curr = new Date();
        var weekStart = new Date(curr.getFullYear(), curr.getMonth(), curr.getDate() - curr.getDay());
        var weekEnd = new Date(curr.getFullYear(), curr.getMonth(), curr.getDate() - curr.getDay() + 6);

        dateStartStr = dateToString(weekStart);
        dateEndStr = dateToString(weekEnd);
      }
      else if (dateOption == "2") {
        var date = new Date();
        dateStartStr = dateToString(new Date(date.getFullYear(), date.getMonth(), 1));
        dateEndStr = dateToString(new Date(date.getFullYear(), date.getMonth() + 1, 0));
      }
      else if (dateOption == "3") {
        var date = new Date();
        dateStartStr = null
        dateEndStr = dateToString(new Date());
      }
      const supplierNameStr = this.searchCriterias.filter(x => x.includes('suppliers')).length > 0 ? this.searchCriterias.filter(x => x.includes('suppliers')).map(x => x.replace(/suppliers=/g, "")) : [];
      const categoryStr = this.searchCriterias.filter(x => x.includes('categories')).length > 0 ? this.searchCriterias.filter(x => x.includes('categories')).map(x => x.replace(/categories=/g, "")) : [];
      const track1Str = this.searchCriterias.filter(x => x.includes('track1')).length > 0 ? this.searchCriterias.filter(x => x.includes('track1')).map(x => x.replace(/track1=/g, "").replace(/%20/g, " ")) : [];
      const track2Str = this.searchCriterias.filter(x => x.includes('track2')).length > 0 ? this.searchCriterias.filter(x => x.includes('track2')).map(x => x.replace(/track2=/g, "")) : [];
      const tagsStr = this.searchCriterias.filter(x => x.includes('tags')).length > 0 ? this.searchCriterias.filter(x => x.includes('tags')).map(x => x.replace(/tags=/g, "").replace(/%20/g, " ")) : [];
      const statusStr = this.searchCriterias.filter(x => x.includes('status')).length > 0 ? this.searchCriterias.filter(x => x.includes('status'))[0].split('=')[1] : false;
      const pageStr = this.searchCriterias.filter(x => x.includes('page')).length > 0 ? this.searchCriterias.filter(x => x.includes('page'))[0]?.split('=')[1] : 0;
      const filterId = this.searchCriterias.filter(x => x.includes('filterId'))[0]?.split('=')[1];
      const submitterNameStr = this.searchCriterias.filter(x => x.includes('submitters')).length > 0 ? this.searchCriterias.filter(x => x.includes('submitters')).map(x => x.replace(/submitters=/g, "")) : [];
      const assignedToNameStr = this.searchCriterias.filter(x => x.includes('assignedTo')).length > 0 ? this.searchCriterias.filter(x => x.includes('assignedTo')).map(x => x.replace(/assignedTo=/g, "")) : [];

      this.processedPage = +pageStr;
      this.processedPage = this.processedPage == 0 || isNaN(this.processedPage) ? 1 : this.processedPage;

      if (dateStartStr) {
        dateStart = moment(dateStartStr, "YYYY-MM-DD").toDate();
      } else {
        dateStart = null;
      }

      if (dateEndStr && dateEndStr != 'null' && dateEndStr != 'undefined') {
        dateEnd = moment(dateEndStr, "YYYY-MM-DD").toDate();
      } else {
        dateEnd = null;
      }


      this.filterParams.suppliers = supplierNameStr && supplierNameStr.length > 0 && supplierNameStr[0] != '' ? supplierNameStr : null;
      this.filterParams.tags = tagsStr && tagsStr.length > 0 && tagsStr[0] != '' ? tagsStr : null;
      this.filterParams.invoiceDateRange = dateStart && dateStart ? [dateStart, dateEnd] : null;
      this.filterParams.categories = categoryStr && categoryStr.length > 0 && categoryStr[0] != '' ? categoryStr : null;
      this.filterParams.trackingOptions1 = track1Str && track1Str.length > 0 && track1Str[0] != '' ? track1Str : null;
      this.filterParams.trackingOptions2 = track2Str && track2Str.length > 0 && track2Str[0] != '' ? track2Str : null;
      this.filterParams.status = !!statusStr && statusStr == 'true' ? true : false;
      this.selectedFilterId = filterId;
      this.filterParams.submitters = submitterNameStr && submitterNameStr.length > 0 && submitterNameStr[0] != '' ? submitterNameStr : null;
      this.filterParams.assignedTo = assignedToNameStr && assignedToNameStr.length > 0 && assignedToNameStr[0] != '' ? assignedToNameStr : null;
      const amountOf = (key: string) => {
        const raw = this.searchCriterias.find(x => x.startsWith(`${key}=`))?.split('=')[1];
        return raw !== undefined && raw !== '' && !isNaN(Number(raw)) ? Number(raw) : null;
      };
      this.filterParams.amountMin = amountOf('amountMin');
      this.filterParams.amountMax = amountOf('amountMax');
      this.setFilterOnSearchState();
      // this.isProcessedTab = this.filterParams.status;
      // this.isInProcessingTab = !this.filterParams.status;
    }
    else {
      this.filterParams.suppliers = null;
      this.filterParams.tags = null;
      this.filterParams.invoiceDateRange = null;
      this.filterParams.categories = null;
      this.filterParams.trackingOptions1 = null;
      this.filterParams.trackingOptions2 = null;
      this.selectedFilterId = null;
      this.filterParams.submitters = null;
      this.filterParams.assignedTo = null;
      this.filterParams.amountMin = null;
      this.filterParams.amountMax = null;
    }

    // Derive the indicator from the parsed criteria — a bare `?isProcessed=true`
    // (plain inbox load) carries no real filter and must not show the chip.
    this.isFilterShown = this.shouldShowFilterIndicator();

    let supplierParam = this.filterParams.suppliers?.length > 0 ? this.filterParams.suppliers?.map(s => `suppliers=${s.replace('&', '%26')}&`).join('').slice(0, -1) : 'suppliers=';
    let track1Param = this.filterParams.trackingOptions1?.length > 0 && this.filterParams.trackingOptions1[0] != "Unassigned tracking" ? this.filterParams.trackingOptions1?.map(s => `track1=${s}&`).join('').slice(0, -1) : 'track1=';
    let track2Param = this.filterParams.trackingOptions2?.length > 0 ? this.filterParams.trackingOptions2?.map(s => `track2=${s}&`).join('').slice(0, -1) : 'track2=';
    let categoryParam = this.filterParams.categories?.length > 0 ? this.filterParams.categories?.map(c => `categories=${c.replace('&', '%26')}&`).join('').slice(0, -1) : 'categories=';
    let tagPart = this.filterParams.tags?.length > 0 ? this.filterParams.tags?.map(t => `tags=${t}&`).join('').slice(0, -1) : 'tags=';
    let dateStartParam = this.filterParams.invoiceDateRange ? `dateStart=${dateToString(this.filterParams.invoiceDateRange[0]) ?? ''}` : 'dateStart=';
    let dateEndParam = this.filterParams.invoiceDateRange ? `dateEnd=${dateToString(this.filterParams.invoiceDateRange[1]) ?? ''}` : 'dateEnd=';
    // Tri-state status: 'all' for the combined view, else processed/unprocessed.
    let statusParam = `status=${this.isCombinedTab ? 'all' : this.isProcessedTab ? 'true' : 'false'}`;
    let submitterParam = this.filterParams.submitters?.length > 0 ? this.filterParams.submitters?.map(s => `submitters=${s.replace('&', '%26')}&`).join('').slice(0, -1) : 'submitters=';
    let assignedToParam = this.filterParams.assignedTo?.length > 0 ? this.filterParams.assignedTo?.map(s => `assignedTo=${s.replace('&', '%26')}&`).join('').slice(0, -1) : 'assignedTo=';
    let amountParam = `amountMin=${this.filterParams.amountMin ?? ''}&amountMax=${this.filterParams.amountMax ?? ''}`;

    this._location.replaceState(`/inbox?isProcessed=${this.selectedInboxStatusFilter === 'for-review' ? false : true}&${supplierParam}&dateOption=${dateOption}&${dateStartParam}&${dateEndParam}&${categoryParam}&${tagPart}&${track1Param}&${track2Param}&${statusParam}&${submitterParam}&${assignedToParam}&${amountParam}`);

    // Unified server-side filtered load — every status view (For Review,
    // Processed, Combined) goes through the search endpoint, which now accepts
    // the tri-state `status` and a free-text `search` param. A filtered view
    // loads its full matching set in one call (pageSize 1000, no pager).
    const searchPart = `search=${encodeURIComponent(this.inboxSearchText || '')}`;
    let filterStr = `?${supplierParam}&${dateStartParam}&${dateEndParam}&${categoryParam}&${tagPart}&${track1Param}&${track2Param}&${statusParam}&${submitterParam}&${assignedToParam}&${amountParam}&${searchPart}`;
    this.filterStr = filterStr;
    let queryParamsStr = `${filterStr}&page=1&pageSize=1000&sort=${this.sortDirection || 'desc'}&organizationId=${this._organizationService.selectedOrganization}`;
    this._searchService.searchInvoices(queryParamsStr)
      .pipe(this.untilThis)
      .subscribe(
        (data: InboxInvoices) => {
          let list = data?.invoices || [];
          // Trim each invoice's lines to the chosen categories (display only —
          // the backend already filters which invoices match).
          if (this.filterParams.categories?.length > 0 && this.filterParams.categories[0] != '') {
            list.forEach(item => {
              if (item.invoiceLines?.length > 0) {
                item.invoiceLines = item.invoiceLines.filter(line => this.filterParams.categories.includes(line.accountName));
              }
            });
          }
          this.prepareProcessedInvoices(list);
          this.filteredTotalAmount = data?.totalAmount || 0;
          if (this.isCombinedTab) {
            this.combinedInvoices = list;
            this.combinedTotalAmount = data?.totalAmount || 0;
            this.filteredCombinedInvoices = [...list];
          } else if (this.isProcessedTab) {
            this.processedInvoices = list;
            this.processedTotalAmount = data?.totalAmount || 0;
            this.filteredProcessedInvoices = [...list];
          } else {
            this.inProcessingInvoices = list;
            this.inProcessingTotalAmount = data?.totalAmount || 0;
            this.filteredInProcessingInvoices = [...list];
          }
          this.setTableEmptyMessage();
          this.loading = false;
        },
        (e: Error) => {
          this.loading = false;
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  setFilterOnSearchState() {
    if (this.selectedFilterId) {
      this.savedFilters.forEach(x => x.isSelectedTab = false);
      var selectedFilter = this.savedFilters.find(x => x.id == this.selectedFilterId);
      if (selectedFilter) {
        selectedFilter.isSelectedTab = true;
        var isFilterAdded = this.selectedSavedFilters.find(x => x.id == selectedFilter.id);
        if (!isFilterAdded) {
          this.selectedSavedFilters.push(selectedFilter);
        }
      }
      else {
        this.selectedSavedFilters = [];
        this.selectedSavedFilters.push(this.defaultFilter);
      }
    }
  }

  private getOrganizationTags() {
    this._tagsService.getTagsByOrganizationId(this.organizationId)
      .pipe(take(1))
      .subscribe(
        (tags: Tag[]) => {
          tags.forEach(x => x.group = 1);
          this.tagList = tags;
          this.filteredTagList = this.tagList;
          this.tagList.splice(0, 0, { tagId: '0', tagName: 'Unassigned', backColor: '', textColor: '', organizationId: this._organizationService.selectedOrganization, group: 0 });
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  toggleSavedFilters() {
    this.isShowSavedFilterList = !this.isShowSavedFilterList;
  }

  appendSearchCriteria(filter) {
    var query = "";
    if (filter.searchCriteria) {
      query = `${filter.searchCriteria}&filterId=${this.selectedFilterId}`;
    }
    else {
      query = `?filterId=${this.selectedFilterId}`
    }
    return query;
  }
  addSelectedFilter(filter) {
    this.resetSearch();
    this.loading = true;
    setTimeout(() => {
      this.selectedFilterId = filter.id;
      var params = this.appendSearchCriteria(filter);
      this._location.replaceState(`/inbox?isProcessed=${this.selectedInboxStatusFilter === 'for-review' ? false : true}&${params}`);
      this.getSearchState(filter.searchCriteria);
      this.selectedSavedFilters.forEach(x => x.isSelectedTab = false);
      filter.isSelectedTab = true;
      this.isFilterShown = true;
      if (this.selectedSavedFilters.indexOf(filter) == -1) {
        this.selectedSavedFilters.push(filter);
      }
      this.toggleSavedFilters();
      this.loading = false;
    }, 1000);
  }


  addFilter() {
    this.showFilterPanel = true;
    this.toggleSavedFilters();
  }

  toggleFilterSidebar() {
    this.showFilterPanel = !this.showFilterPanel;
    this.isEditMode = false;
    if (!this.showFilterPanel) return;

    // Opening over an applied filter shows its conditions, not a blank slate.
    setTimeout(() => {
      const saved = this.activeSavedFilter;
      if (saved) {
        this.inboxFilterComponent?.openForEdit(saved);
      } else if (this.currentFilterCriteria) {
        this.inboxFilterComponent?.openForCriteria(this.currentFilterCriteria);
      } else {
        this.inboxFilterComponent?.openForNew();
      }
    });
  }

  // A column header's filter icon opens the panel with a condition on that field.
  openFilterForField(field: string) {
    const fields: { [key: string]: InboxConditionField } = {
      supplier: 'supplier', date: 'date', category: 'category', tags: 'tag',
      tracking1: 'job', tracking2: 'job2'
    };
    this.showFilterPanel = true;
    setTimeout(() => this.inboxFilterComponent?.openForField(fields[field] || 'supplier', this.currentFilterCriteria));
  }

  // Handle header inline filter apply events from the table
  onHeaderFilterApplied(event: { field: string, value: any }) {
    const field = event?.field;
    const value = event?.value;


    switch (field) {
      case 'supplier':
        this.filterParams.suppliers = value && value.length > 0 ? value : null;
        break;
      case 'category':
        this.filterParams.categories = value && value.length > 0 ? value : null;
        break;
      case 'tags':
        this.filterParams.tags = value && value.length > 0 ? value : null;
        break;
      case 'date':
        if (value && Array.isArray(value) && value.length === 2) {
          this.filterParams.invoiceDateRange = [new Date(value[0]), new Date(value[1])];
        } else {
          this.filterParams.invoiceDateRange = null;
        }
        break;
      case 'tracking1':
        this.filterParams.trackingOptions1 = value && value.length > 0 ? value : null;
        break;
      case 'tracking2':
        this.filterParams.trackingOptions2 = value && value.length > 0 ? value : null;
        break;
      case 'submitter':
        this.filterParams.submitters = value && value.length > 0 ? value : null;
        break;
      case 'assignedTo':
        this.filterParams.assignedTo = value && value.length > 0 ? value : null;
        break;
      case 'type':
        this.filterParams.types = value && value.length > 0 ? value : null;
        break;
      case 'amount':
        this.filterParams.amountMin = value?.min ?? null;
        this.filterParams.amountMax = value?.max ?? null;
        break;
      default:
        break;
    }

    // Reflect filter state from the actual criteria — applying an empty
    // header selection (or clearing the last one) must not leave a stale
    // "Filters Applied" indicator behind.
    this.isFilterShown = this.shouldShowFilterIndicator();

    // Apply filters locally for in-processing tab
    if (this.isInProcessingTab) {
      this.filteredInProcessingInvoices = [...this.filterInProcessingInvoices(this.inProcessingInvoices)];
      this.setTableEmptyMessage();
    }

    // For processed tab, rebuild query and call search API
    if (this.isProcessedTab) {
      let dateOption = 'dateOption=';
      if (this.filterParams.invoiceDateRange) {
        dateOption = 'dateOption=custom';
      }

      let supplierParam = this.filterParams.suppliers?.length > 0 ? this.filterParams.suppliers?.map(s => `suppliers=${s.replace('&', '%26')}&`).join('').slice(0, -1) : 'suppliers=';
      let track1Param = this.filterParams.trackingOptions1?.length > 0 && this.filterParams.trackingOptions1[0] != "Unassigned tracking" ? this.filterParams.trackingOptions1?.map(s => `track1=${s}&`).join('').slice(0, -1) : 'track1=';
      let track2Param = this.filterParams.trackingOptions2?.length > 0 ? this.filterParams.trackingOptions2?.map(s => `track2=${s}&`).join('').slice(0, -1) : 'track2=';
      let categoryParam = this.filterParams.categories?.length > 0 ? this.filterParams.categories?.map(c => `categories=${c.replace('&', '%26')}&`).join('').slice(0, -1) : 'categories=';
      let tagPart = this.filterParams.tags?.length > 0 ? this.filterParams.tags?.map(t => `tags=${t}&`).join('').slice(0, -1) : 'tags=';
      let typeParam = this.filterParams.types?.length > 0 ? this.filterParams.types?.map(t => `Type=${t.replace('&', '%26')}&`).join('').slice(0, -1) : 'Type=';
      let dateStartParam = this.filterParams.invoiceDateRange ? `dateStart=${dateToString(this.filterParams.invoiceDateRange[0]) ?? ''}` : 'dateStart=';
      let dateEndParam = this.filterParams.invoiceDateRange ? `dateEnd=${dateToString(this.filterParams.invoiceDateRange[1]) ?? ''}` : 'dateEnd=';
      let statusParam = `status=${this.isProcessedTab ? 'true' : 'false'}`;
      let submitterParam = this.filterParams.submitters?.length > 0 ? this.filterParams.submitters?.map(s => `submitters=${s.replace('&', '%26')}&`).join('').slice(0, -1) : 'submitters=';
      let assignedToParam = this.filterParams.assignedTo?.length > 0 ? this.filterParams.assignedTo?.map(s => `assignedTo=${s.replace('&', '%26')}&`).join('').slice(0, -1) : 'assignedTo=';
      let amountMinPart = (this.filterParams.amountMin !== null && this.filterParams.amountMin !== undefined) ? `amountMin=${this.filterParams.amountMin}` : 'amountMin=';
      let amountMaxPart = (this.filterParams.amountMax !== null && this.filterParams.amountMax !== undefined) ? `amountMax=${this.filterParams.amountMax}` : 'amountMax=';

      this._location.replaceState(`/inbox?isProcessed=${this.selectedInboxStatusFilter === 'for-review' ? false : true}&${supplierParam}&dateOption=${dateOption}&${dateStartParam}&${dateEndParam}&${categoryParam}&${tagPart}&${typeParam}&${track1Param}&${track2Param}&${statusParam}&${submitterParam}&${assignedToParam}&${amountMinPart}&${amountMaxPart}`);

      let filterStr = `?${supplierParam}&${dateStartParam}&${dateEndParam}&${categoryParam}&${tagPart}&${typeParam}&${track1Param}&${track2Param}&${statusParam}&${submitterParam}&${amountMinPart}&${amountMaxPart}`;
      this.filterStr = filterStr;
      let queryParamsStr = `${filterStr}&page=${this.processedPage}&pageSize=${this.processedPageSize}&organizationId=${this._organizationService.selectedOrganization}`;
      this._searchService.searchInvoices(queryParamsStr)
        .pipe(this.untilThis)
        .subscribe(
          (data: InboxInvoices) => {
            let list = data.invoices;

            if (this.filterParams.tags && this.filterParams.tags?.length > 0 && this.filterParams.tags[0] != '') {
              if (this.filterParams.tags.includes('Unassigned tags')) {
                list = list.filter(i => !i.tags || i.tags?.length == 0);
              }
            }

            if (this.filterParams.trackingOptions1 && this.filterParams.trackingOptions1.length > 0 && this.filterParams.trackingOptions1[0] != '') {
              if (this.filterParams.trackingOptions1.includes('Unassigned tracking')) {
                list = list.filter(i => i.invoiceLines.length > 0 ? i.invoiceLines.some(line => line.trackingOptions?.length == 0) : true);
              } else {
                list = list.filter(i => i.invoiceLines.some(line => line.trackingOptions?.length > 0 ? line.trackingOptions.some(option => this.filterParams.trackingOptions1.includes(option)) : false));
              }
            }
            if (this.filterParams.submitters && this.filterParams.submitters.length > 0 && this.filterParams.submitters[0] != '') {
              list = list.filter(i => this.filterParams.submitters.includes(i.submitterName));
            }

            // Apply Type filter if present
            if (this.filterParams.types && this.filterParams.types.length > 0) {
              list = list.filter(i => this.filterParams.types.includes(i.type));
            }

            this.filteredProcessedInvoices = list;
            this.prepareProcessedInvoices(this.filteredProcessedInvoices);

            if (this.filterParams.categories?.length > 0 && this.filterParams.categories[0] != '') {
              this.filteredProcessedInvoices.forEach(item => {
                if (item.invoiceLines?.length > 0) {
                  item.invoiceLines = item.invoiceLines?.filter(line => this.filterParams.categories.includes(line.accountName.replace('&', '%26')));
                }
              });
            }

            this.filteredTotalAmount = data.totalAmount;
            this.isFilterShown = this.shouldShowFilterIndicator();
            this.loading = false;
            this.filterDataLoading = false;
            this.setTableEmptyMessage();
          },
          (e: Error) => {
            this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
            this.loading = false;
            this.filterDataLoading = false;
          }
        );
    }
  }

  getAllSavedFilters() {
    this._filterService.getAllSavedFilters("Filter/GetAllSavedFilters", 1, this._organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(
        (filters) => {
          this.savedFilters = filters;
          filters.filter(x => x.isPinned == true).forEach(x => this.selectedSavedFilters.push(x));
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  onFilterTabSelect(filter) {
    this.resetSearch();
    this.loading = true;
    var params = this.appendSearchCriteria(filter);
    this._location.replaceState(`/inbox?isProcessed=${this.selectedInboxStatusFilter === 'for-review' ? false : true}&${params}`);
    this.getSearchState(filter.searchCriteria);
    this.selectedSavedFilters.forEach(x => x.isSelectedTab = false);
    filter.isSelectedTab = true;
    if (filter.name == 'All') {
      this.isFilterShown = false;
      if (this.isInProcessingTab == true) {
        this.getInProcessingInvoices();
      }
      else if (this.isProcessedTab == true) {
        this.getProcessedInvoices();
      }
    }
    else {
      this.isFilterShown = true;
    }
    // setTimeout(() => {
    //   this.loading = false;
    // }, 400);
  }
  editFilter(filter) {
    this.selectedFilterId = filter.id;
    this.showFilterPanel = true;
    this.isEditMode = true;
    setTimeout(() => this.inboxFilterComponent?.openForEdit(filter));
  }

  closeFilter(filter) {
    this.loading = true;
    this.isFilterShown = false;
    this.selectedSavedFilters = this.selectedSavedFilters.filter(item => item !== filter);
    this.onFilterTabSelect(this.selectedSavedFilters[0]);
  }

  deletedFilterRemove() {
    this.selectedSavedFilters = Object.values(this.selectedSavedFilters).filter(g => g.id != $("#hdFilterId").val());
    this.savedFilters = Object.values(this.savedFilters).filter(g => g.id != $("#hdFilterId").val());
    this.onFilterTabSelect(this.selectedSavedFilters[0]);
  }

  showExportMenu() {
    this.isExportMenuShown = true;
  }

  exportRecords() {
    // SD-1235: the list on screen — the combined view used to export the processed list instead.
    let checkedInvoices = this.activeFilteredInvoices;

    if (this.exportOption == 1) {
      checkedInvoices = checkedInvoices.filter(inv => inv.checked);
      const blob = new Blob([this.saveDataInCSV(checkedInvoices)], { type: 'text/csv;charset=utf-8' })
      saveAs(blob, "Invoices.csv");
    }
    else {
      this._invoicesService.getInboxInvoicesUploadedByUser(this._organizationService.selectedOrganization, 1, 10000, 'asc', null, null)
        .pipe(this.untilThis)
        .subscribe(
          (invoices: InboxInvoices) => {
            checkedInvoices = invoices.invoices;
            const blob = new Blob([this.saveDataInCSV(checkedInvoices)], { type: 'text/csv;charset=utf-8' })
            saveAs(blob, "Invoices.csv");
          },
          (e: Error) => {
            this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
          }
        )
    }


  }

  /** SD-1235: one CSV field, quoted when it holds a comma, quote or line break. */
  private csvField(value: any): string {
    const text = value == null ? '' : String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }

  saveDataInCSV(data: InvoiceShort[]) {
    if (data.length == 0) {
      return '';
    }

    let propertyNames = ['Supplier', 'Date', 'Category', 'Date Submitted', 'Type', 'Due Date', 'Status'];
    if (this.selectedTrackingCategories.length > 0) {
      this.selectedTrackingCategories.forEach((item) => {
        propertyNames.push(item.name);
      })
    }
    propertyNames.push('Subtotal');
    propertyNames.push('Tax');
    propertyNames.push('Total');
    propertyNames.push('Tags');
    propertyNames.push('Invoice Link');
    let rowWithPropertyNames = propertyNames.join(',') + '\n';
    let csvContent = rowWithPropertyNames;

    let rows: string[] = [];

    data.forEach((item) => {
      let values: string[] = [];

      values.push(item.supplierName ?? '');
      values.push(item.date != undefined && item.date != null ? item.date.toString() : '');
      values.push(item.accountName);
      values.push(item.createdAt != undefined && item.createdAt != null ? item.createdAt.toString() : '');
      values.push(item.type != undefined && item.type != null ? item.type.toString() : '');
      values.push(item.dueDate != undefined && item.dueDate != null ? item.dueDate.toString() : '');
      values.push(!item.isProcessed ? 'Review' : 'Processed');
      if (this.selectedTrackingCategories.length > 0) {
        if (item.invoiceLines.length > 0 && item.invoiceLines[0].trackingOptions.length > 0) {
          values.push(item.invoiceLines[0].trackingOptionNames[0].toString());
        } else {
          values.push('');
        }

        if (item.invoiceLines.length > 0 && item.invoiceLines[0].trackingOptions.length > 1) {
          values.push(item.invoiceLines[0].trackingOptionNames[1].toString());
        } else {
          values.push('');
        }
      }
      values.push(item.subtotal != undefined && item.subtotal != null ? item.subtotal.toFixed(2).toString() : '');
      values.push(item.totalTax != undefined && item.totalTax != null ? item.totalTax.toFixed(2).toString() : '');
      values.push(item.amountDue != undefined && item.amountDue != null ? item.amountDue.toFixed(2).toString() : '');
      values.push(item.tags != undefined && item.tags != null ? item.tags.map(function (d) { return d.tagName }).join(';') : '');
      values.push(this.configService.apiURL + "Invoices/downloadInvoice?id=" + item.invoicePdfUrl + "&organisationId=" + this._organizationService.selectedOrganization);

      rows.push(values.map(v => this.csvField(v)).join(','));
    });
    csvContent += rows.join('\n');

    return csvContent;
  }

  showDeleteConfirm() {
    const count = this.checkedInProcessingInvoices?.length || 0;
    if (!count || this.isDeletingSelection) return;
    const what = count === 1 ? 'this document' : `these ${count} documents`;
    this._confirmationDialogService.confirm('Confirm', `Do you want to delete ${what}?`, 'Yes', 'No', '')
      .then((confirm) => {
        if (confirm) {
          this.deleteInvoice();
        }
      });
  }

  /** SD-1235: deletes the selection together and updates the list once when all have answered —
   *  was one list rewrite and one error toast per document, racing each other. */
  deleteInvoice() {
    if (!this.isInProcessingTab) return;
    const checkedInvoices = this.filteredInProcessingInvoices.filter(inv => inv.checked);
    if (!checkedInvoices.length) return;

    this.isDeletingSelection = true;
    forkJoin(checkedInvoices.map(invoice =>
      this._invoiceService.deleteInvoice(invoice.id.toString()).pipe(
        take(1),
        map(() => invoice.id),
        catchError(() => of(null))
      )))
      .pipe(this.untilThis, finalize(() => this.isDeletingSelection = false))
      .subscribe((results) => {
        const deleted = new Set(results.filter(id => id != null).map(id => id.toString()));
        deleted.forEach(id => this._inboxInvoicesStorage.deleteInvoiceIdFromStorage(id));
        this.filteredInProcessingInvoices = this.filteredInProcessingInvoices.filter(g => !deleted.has(g.id?.toString()));
        this.rowCheckboxClickEvent();

        const failed = checkedInvoices.length - deleted.size;
        if (failed > 0) {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error',
            `${failed} of ${checkedInvoices.length} could not be deleted`);
        }
      });
  }

  onSearchBox(event) {
    if (this.storedInvoices.length == 0) {
      if (this.isInProcessingTab == true) {
        this.storedInvoices = this.filteredInProcessingInvoices;
      }
      else if (this.isProcessedTab == true) {
        this.storedInvoices = this.filteredProcessedInvoices;
      }
    }
    if (event.key != null && event.key != '' && !this.isFilterShown) {
      this.searchText = event.target.value.toLowerCase();
      if (this.searchText != null && (this.searchText == '' || (this.searchText != '' && this.searchText.length > 2))) {
        if (this.isInProcessingTab == true) {
          this.getInProcessingInvoices();
        }
        else if (this.isProcessedTab == true) {
          this.getProcessedInvoices();
        }
      }
    }
    else if (event.key != null && event.key != '' && this.isFilterShown) {
      this.searchText = event.target.value.toLowerCase();
      if (this.searchText != null && (this.searchText == '' || (this.searchText != '' && this.searchText.length > 2))) {
        if (this.storedInvoices.length == 0) {
          if (this.isInProcessingTab == true) {
            this.storedInvoices = this.filteredInProcessingInvoices;
          }
          else if (this.isProcessedTab == true) {
            this.storedInvoices = this.filteredProcessedInvoices;
          }
        }
        var result = this.storedInvoices.filter(x =>
          x.invoiceNumber?.toLowerCase().includes(event.target.value.toLowerCase()) || x.supplierName?.toLowerCase().includes(event.target.value.toLowerCase()) ||
          x.accountName?.toLowerCase().includes(event.target.value.toLowerCase()) ||
          x.categories?.filter(c => c?.toLowerCase().includes(event.target.value.toLowerCase()))
          || (x.tags.length > 0 && x.tags?.filter(t => t?.tagName?.toLowerCase().includes(event.target.value.toLowerCase())).length > 0 &&
            x.tags?.filter(t => t?.tagName?.toLowerCase().includes(event.target.value.toLowerCase())))
        );
        this.filteredInProcessingInvoices = result;
        this.filteredProcessedInvoices = result;
      }
      //var result = this.storedInvoices.filter(x =>
      //  x.invoiceNumber?.toLowerCase().includes(event.target.value.toLowerCase()) || x.supplierName?.toLowerCase().includes(event.target.value.toLowerCase()) ||
      //  x.accountName?.toLowerCase().includes(event.target.value.toLowerCase()) ||
      //  x.categories?.filter(c => c?.toLowerCase().includes(event.target.value.toLowerCase()))
      //  || (x.tags.length > 0 && x.tags?.filter(t => t?.tagName?.toLowerCase().includes(event.target.value.toLowerCase())).length > 0 &&
      //    x.tags?.filter(t => t?.tagName?.toLowerCase().includes(event.target.value.toLowerCase())))
      //);
      //this.filteredInProcessingInvoices = result;
      //this.filteredProcessedInvoices = result;
    }
  }
  resetSearch() {
    this.pageSidebarComponent.searchInput.nativeElement.value = "";
    this.searchText = "";
    // Reset our search filter but keep the current tab filter
    this.inboxSearchText = "";
    // Don't reset filter to 'all', keep current tab's filter
    if (this.isInProcessingTab) {
      this.selectedInboxStatusFilter = "for-review";
    } else if (this.isProcessedTab) {
      this.selectedInboxStatusFilter = "processed";
    } else {
      this.selectedInboxStatusFilter = "for-review"; // default
    }
    if (this.storedInvoices.length !== 0) {
      if (this.isInProcessingTab == true) {
        this.inProcessingInvoices = this.storedInvoices;
      }
      else if (this.isProcessedTab == true) {
        this.inProcessingInvoices = this.storedInvoices;
      }
    }
  }

  onSearchChange(event: any): void {
    var x = event.key;
    var isClear = (event.type == 'click' && event.target.value != '');
    if (x == "Enter" || isClear) {
      this.isFilterShown = true;
      this.loading = true;
      this.searchText = isClear ? '' : event.target.value;
      if (this.storedInvoices.length == 0) {
        if (this.isInProcessingTab == true) {
          this.storedInvoices = this.filteredInProcessingInvoices;
        }
        else if (this.isProcessedTab == true) {
          this.storedInvoices = this.filteredProcessedInvoices;
        }
      }
      if (((event.key != null && event.key != '') || event.key == undefined) && !this.isFilterShown) {
        if (event.key != undefined)
          this.searchText = event.target.value.toLowerCase();
        else
          this.searchText = "";
        if (this.searchText != null && (this.searchText == '' || (this.searchText != '' && this.searchText.length > 2))) {
          if (this.isInProcessingTab == true) {
            this.storedInvoices = this.inProcessingInvoices;
          }
          else if (this.isProcessedTab == true) {
            this.storedInvoices = this.processedInvoices;
          }
          if (this.isInProcessingTab == true) {
            this.getInProcessingInvoices();
          }
          else if (this.isProcessedTab == true) {
            this.getProcessedInvoices();
          }
        }
      }
      else if (((event.key != null && event.key != '') || event.key == undefined) && this.isFilterShown) {
        if (event.key != undefined)
          this.searchText = event.target.value.toLowerCase();
        else
          this.searchText = "";
        if (this.searchText != null && (this.searchText == '' || (this.searchText != '' && this.searchText.length > 2))) {
          if (this.isInProcessingTab == true) {
            this.getInProcessingInvoices();
          }
          else if (this.isProcessedTab == true) {
            this.getProcessedInvoices();
          }
          var result = this.storedInvoices;
          if (this.searchText != "") {
            result = this.storedInvoices.filter(x =>
              x.invoiceNumber?.toLowerCase().includes(event.target.value.toLowerCase()) || x.supplierName?.toLowerCase().includes(event.target.value.toLowerCase()) ||
              x.accountName?.toLowerCase().includes(event.target.value.toLowerCase()) ||
              x.categories?.filter(c => c?.toLowerCase().includes(event.target.value.toLowerCase()))
              || (x.tags.length > 0 && x.tags?.filter(t => t?.tagName?.toLowerCase().includes(event.target.value.toLowerCase())).length > 0 &&
                x.tags?.filter(t => t?.tagName?.toLowerCase().includes(event.target.value.toLowerCase())))
            );
          }
          this.filteredInProcessingInvoices = result;
          this.filteredProcessedInvoices = result;
        }
      }
    }

  }

  publishInvoiceFromTable(invoice: InvoiceShort) {
    this._getInvoice(invoice);
  }

  private _getInvoice(invoice: InvoiceShort) {
    if (!invoice.id) {
      // Nothing to fetch, so nothing will ever clear the row's spinner.
      invoice.isPublishing = false;
      return;
    }

    this._invoiceService
      .getInvoiceById(invoice.id.toString())
      .pipe(take(1))
      .subscribe(
        (invoicea: Invoice) => {
          this.invoice = invoicea;
          this.handleInvoiceType();
          this.publishInvoice(invoice);
        },
        (e: Error) => {
          // The publish never started, so the error path that resets the row
          // does not run — leaving the button stuck on "Publishing", which
          // then refuses every further click.
          this.afterPublishError(invoice, e);
        }
      );
  }

  private handleInvoiceType() {
    if (this.invoice.type !== null) {
      let invoiceType = this.invoice.type.toString();
      if (invoiceType == 'Credit Note' || invoiceType == 'ACCPAYCREDIT') {
        this.type = 'ACCPAYCREDIT';
      } else if (invoiceType == 'Supplier Invoice' || invoiceType == 'ACCPAY') {
        this.type = 'ACCPAY';
      } else if (invoiceType == 'Customer Invoice' || invoiceType == 'ACCREC') {
        this.type = 'ACCREC';
      } else if (invoiceType == 'Expense Claim' || invoiceType == 'EXPCLAIMS') {
        this.type = 'EXPCLAIMS';
      } else if (invoiceType == 'Receipt' || invoiceType == 'EXP') {
        this.type = 'EXP';
      }
    } else {
      this.type = 'ACCPAY';
    }
  }

  publishInvoice(invoice: InvoiceShort) {
    // Snapshot the request payload + identifying fields synchronously, BEFORE
    // any concurrent click can clobber the shared `this.invoice` / `this.type`.
    // Without this, two near-simultaneous publishes share state — the first
    // callback ends up filtering the SECOND invoice out of the in-processing
    // list (because `this.invoice.id` has already been overwritten), which
    // both prevents the spinner from clearing on the original row and briefly
    // renders that other row in both lists at once (the "duplicate spinner").
    const type = this.type;
    const requestPayload =
      type === 'ACCPAYCREDIT' ? this.getCreditNoteObject() :
      type === 'EXP'           ? null /* receipt builds its own payload */ :
                                 this.getInvoiceObject();

    if (type === 'ACCPAYCREDIT') {
      this._invoiceService
        .publishCreditNote(requestPayload as any)
        .pipe(take(1))
        .subscribe(
          () => this.afterPublishSuccess(invoice),
          (e: Error) => this.afterPublishError(invoice, e)
        );
    } else if (type === 'EXP') {
      this.publishReceipt(invoice);
    } else {
      this._invoiceService
        .publishInvoice(requestPayload as any)
        .pipe(take(1))
        .subscribe(
          () => this.afterPublishSuccess(invoice),
          (e: Error) => this.afterPublishError(invoice, e)
        );
    }
  }

  publishReceipt(invoice: InvoiceShort) {
    // Same snapshot rule as publishInvoice — capture the payload before any
    // other click can mutate `this.invoice`.
    const requestPayload = this.IsQuickBook
      ? this.getReceiptObjectQBO()
      : this.getReceiptObject();

    if (this.IsQuickBook) {
      this._invoiceService
        .publishReceiptQBO(requestPayload as any)
        .pipe(take(1))
        .subscribe(
          () => this.afterPublishSuccess(invoice),
          (e: Error) => this.afterPublishError(invoice, e)
        );
    } else {
      this._invoiceService
        .publishReceipt(requestPayload as any)
        .pipe(take(1))
        .subscribe(
          () => {
            // Receipt path refetches lists rather than mutating in place;
            // still need to clear the per-row publishing flag.
            this.getInProcessingInvoices();
            this.getProcessedInvoices();
            invoice.isPublishing = false;
            if (this.tableComponent && this.tableComponent.currentPublishId === invoice.id?.toString()) {
              this.tableComponent.isPublishing = false;
              this.tableComponent.currentPublishId = '';
            }
          },
          (e: Error) => this.afterPublishError(invoice, e)
        );
    }
  }

  /**
   * Shared post-publish success path. Uses the *clicked row's* id to move it
   * between lists — never `this.invoice.id`, which may have been overwritten
   * by a concurrent publish since the request started.
   */
  private afterPublishSuccess(invoice: InvoiceShort) {
    const movedId = invoice.id;
    // The row is now processed - reflected in place so the combined view
    // re-renders it correctly without a refetch.
    invoice.isProcessed = true;
    this.inProcessingInvoices = this.inProcessingInvoices.filter(inv => inv.id != movedId);
    this.filteredInProcessingInvoices = this.filteredInProcessingInvoices.filter(inv => inv.id != movedId);
    if (!this.processedInvoices.some(inv => inv.id == movedId)) {
      this.processedInvoices.unshift(invoice);
    }
    if (!this.filteredProcessedInvoices.some(inv => inv.id == movedId)) {
      this.filteredProcessedInvoices.unshift(invoice);
    }
    this.filteredTotalAmount = this.filteredTotalAmount + (invoice.amountDue ?? 0);
    invoice.isPublishing = false;
    // Only clear the table-wide flag if it was tracking THIS row.
    if (this.tableComponent && this.tableComponent.currentPublishId === movedId?.toString()) {
      this.tableComponent.isPublishing = false;
      this.tableComponent.currentPublishId = '';
    }
  }

  /** Shared post-publish error path. Clears row state so the user can retry. */
  private afterPublishError(invoice: InvoiceShort, e: Error) {
    invoice.isPublishing = false;
    if (this.tableComponent && this.tableComponent.currentPublishId === invoice.id?.toString()) {
      this.tableComponent.isPublishing = false;
      this.tableComponent.currentPublishId = '';
    }
    if (e?.message) {
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Error,
        'Error',
        e.message
      );
    }
  }

  getInvoiceObject(isSaving: boolean = false): CreatedInvoiceWithLines {
    let invoice = new CreatedInvoiceWithLines();
    invoice.amountDue = this.invoice.amountDue ?? 0;
    invoice.currency = this.invoice.currency;
    invoice.date = this.invoice.date;
    invoice.dueDate = isSaving
      ? this.invoice.dueDate
      : !this.invoice.dueDate
        ? this.invoice.date
        : this.invoice.dueDate;
    invoice.supplierName = this.invoice.supplierName;
    invoice.supplierId = this.invoice.supplierId;
    (invoice.id = this.invoice.id.toString() ?? ''),
      (invoice.invoiceNumber = this.invoice.invoiceNumber);
    invoice.organisationId =
      this.invoice.organisationId ??
      this._organizationService.selectedOrganization;
    invoice.paymentStatus = this.invoice.paymentStatus;
    invoice.pdfUrl = this.invoice.pdfUrl;
    invoice.subTotal = this.invoice.subTotal ?? 0;
    invoice.totalTax = this.invoice.totalTax ?? 0;
    invoice.type = this.invoice.type;
    invoice.invoiceLines = this.invoice.invoiceLines;
    invoice.InvoiceOrCreditNote = this.invoice.type;
    invoice.paidBy = this.invoice.paidBy;
    invoice.expenseReportId = this.invoice.expenseReportId;
    invoice.expenseReportName = this.invoice.expenseReportName;
    invoice.paymentAccountNumber = this.invoice.paymentAccountNumber;
    invoice.paymentDate = this.invoice.paymentDate;
    if (invoice.invoiceLines?.length > 0) {
      for (let line of invoice.invoiceLines) {
        line.organisationId =
          this.invoice.organisationId ??
          this._organizationService.selectedOrganization;
        if (line.totalTax == null) {
          line.totalTax = 0;
        }
      }
    }

    return invoice;
  }

  getCreditNoteObject(isSaving: boolean = false): CreditNoteDTO {
    let creditNote = new CreditNoteDTO(
      this._organizationService.selectedOrganization
    );
    this.initCreditNoteLine();
    Object.keys(creditNote).reduce(
      (a, k) => ((a[k] = this.creditNote[k]), a),
      creditNote
    );
    creditNote.contact = {
      contactID: this.invoice.supplierId,
      name: this.invoice.supplierName,
      status: 'ACTIVE',
    };
    creditNote.invoiceID = this.invoice.id;
    creditNote.date = this.invoice.date;
    creditNote.subTotal = this.invoice.subTotal;
    creditNote.totalTax = this.invoice.totalTax;
    creditNote.total = this.invoice.amountDue;
    creditNote.lineItems = this.lineItemList;
    creditNote.PdfUrl = this.invoice.pdfUrl;
    creditNote.creditNoteID = this.invoice.id.toString();
    creditNote.InvoiceOrCreditNote = 'Credit Note';
    creditNote.currency = this.invoice.currency;
    creditNote.currencyCode = this.invoice.currency;
    creditNote.invoiceNumber = this.invoice.invoiceNumber;
    return creditNote;
  }

  initCreditNoteLine() {
    if (this.invoice.invoiceLines.length > 0) {
      for (let i = 0; i < this.invoice.invoiceLines.length; i++) {
        var item = {
          description:
            this.invoice?.description === undefined ||
              this.invoice?.description === null ||
              this.invoice?.description === ''
              ? this.invoice.invoiceNumber
              : this.invoice?.description,
          trackingOptions: this.invoice.invoiceLines[i].trackingOptions,
          unitAmount:
            this.invoice?.amountDue -
            (this.invoice?.totalTax === null ? 0 : this.invoice?.totalTax),
          taxType: this.invoice.invoiceLines[i].taxId,
          taxAmount: this.invoice?.totalTax === null ? 0 : this.invoice?.totalTax,
          lineAmount: this.invoice.amountDue,
          accountCode: this.invoice.invoiceLines[i].accountId,
          quantity: 1,
        };

        this.lineItemList.push(item);
      }
    }
  }

  getReceiptObject(isSaving: boolean = false): BankTransactionDTO {
    let bankTransaction = new BankTransactionDTO(
      this._organizationService.selectedOrganization
    );
    Object.keys(bankTransaction).reduce(
      (a, k) => ((a[k] = this.bankTransation[k]), a),
      bankTransaction
    );

    let currentPaymentFrom: any = this.bankAccountList.find(x => x.accountID == this.invoice.paymentAccountNumber);
    bankTransaction.bankAccount = {
      accountID: currentPaymentFrom?.accountID ?? this.invoice?.accountID,
      name: currentPaymentFrom?.name ?? this.invoice?.accountName,
      type:
        currentPaymentFrom?.type == 'CREDITCARD'
          ? 'BANK'
          : currentPaymentFrom?.type,
      currencyCode: this.invoice.currency,
      bankAccountNumber: currentPaymentFrom?.bankAccountNumber,
    };
    bankTransaction.contact = {
      contactID: this.invoice.supplierId,
      name: this.invoice.supplierName,
      status: 'ACTIVE',
    };
    bankTransaction.date = this.invoice.date;
    bankTransaction.subTotal = this.invoice.subTotal;
    bankTransaction.totalTax = this.invoice.totalTax;
    bankTransaction.total = this.invoice.amountDue;
    bankTransaction.PdfUrl = this.invoice.pdfUrl;
    bankTransaction.invoiceId = this.invoice.id.toString();
    bankTransaction.InvoiceOrCreditNote = 'Receipt';
    bankTransaction.type = DocumentType['Receipt'];
    bankTransaction.currency = this.invoice.currency;
    bankTransaction.currencyCode = this.invoice.currency;
    bankTransaction.invoiceNumber = this.invoice.invoiceNumber;
    bankTransaction.paymentAccountNumber = this.invoice.paymentAccountNumber;
    bankTransaction.paymentStatus = this.invoice.paymentStatus;
    bankTransaction.lineItems = this.invoice.invoiceLines;
    if (bankTransaction.lineItems?.length > 0) {
      for (let line of bankTransaction.lineItems) {
        line.accountName = '';
        line.organisationId =
          this.invoice.organisationId ??
          this._organizationService.selectedOrganization;
        if (line.totalTax == null) {
          line.totalTax = 0;
        }
      }
    }
    return bankTransaction;
  }

  getReceiptObjectQBO(isSaving: boolean = false): CreatedInvoiceWithLines {
    let invoice = new CreatedInvoiceWithLines();
    invoice.amountDue = this.invoice.amountDue ?? 0;
    invoice.currency = this.invoice.currency;
    invoice.date = this.invoice.date;
    invoice.dueDate = isSaving
      ? this.invoice.dueDate
      : !this.invoice.dueDate
        ? this.invoice.date
        : this.invoice.dueDate;
    invoice.supplierName = this.invoice.supplierName;
    invoice.supplierId = this.invoice.supplierId;
    (invoice.id = this.invoice.id.toString() ?? ''),
      (invoice.invoiceNumber = this.invoice.invoiceNumber);
    invoice.organisationId =
      this.invoice.organisationId ??
      this._organizationService.selectedOrganization;
    invoice.paymentStatus = this.invoice.paymentStatus;
    invoice.pdfUrl = this.invoice.pdfUrl;
    invoice.subTotal = this.invoice.subTotal ?? 0;
    invoice.totalTax = this.invoice.totalTax ?? 0;
    invoice.type = this.invoice.type;
    invoice.invoiceLines = this.invoice.invoiceLines;
    invoice.InvoiceOrCreditNote = this.invoice.type;
    invoice.paidBy = this.invoice.paidBy;
    invoice.expenseReportId = this.invoice.expenseReportId;
    invoice.expenseReportName = this.invoice.expenseReportName;
    invoice.paymentAccountNumber = this.invoice.paymentAccountNumber;
    invoice.paymentDate = this.invoice.paymentDate;
    if (invoice.invoiceLines?.length > 0) {
      for (let line of invoice.invoiceLines) {
        line.organisationId =
          this.invoice.organisationId ??
          this._organizationService.selectedOrganization;
        if (line.totalTax == null) {
          line.totalTax = 0;
        }
      }
    }

    return invoice;
  }

  GetBankAcoountDetails() {
    this._organizationService
      .GetBankAccountDetails(
        'Organisation/GetBankAccountDetails',
        this._organizationService.selectedOrganization
      ).pipe(this.untilThis)
      .subscribe(
        (response: any) => {
          if (response.status == 0) {
            this.bankAccountList = response.data;
            if (this.bankAccountList?.length > 0) {
              this.bankAccountList.forEach((payAcc) => {
                payAcc.accountID = payAcc.accountID.toLowerCase();
              });
            }
          }
        },
        (err) => {
          this.bankAccountList = [];
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            err.message
          );
        }
      );
  }

  // Update toggle method for submitter column to accept a parameter
  toggleSubmitterColumn(value?: boolean) {
    if (value !== undefined) {
      this.showSubmitterColumn = value;
    } else {
      this.showSubmitterColumn = !this.showSubmitterColumn;
    }
    // Save preference to localStorage to persist the setting
    localStorage.setItem('showSubmitterColumn', String(this.showSubmitterColumn));
    this.columnVisibility = {
      ...this.columnVisibility,
      submitter: this.showSubmitterColumn
    };
    this.persistColumnVisibility();
  }

  private getTrackingColumnLabel(index: number): string {
    const category = this.selectedTrackingCategories?.[index];
    return category?.name || `Tracking Category ${index + 1}`;
  }

  private updateColumnOptions(): void {
    const trackingColumns: ColumnOption[] = [
      { key: 'tracking1', label: this.getTrackingColumnLabel(0) },
      { key: 'tracking2', label: this.getTrackingColumnLabel(1) }
    ];
    this.columnOptions = [
      ...this.baseColumnOptions,
      { key: 'submitter', label: 'Submitter' },
      ...trackingColumns
    ];
  }

  /** The Columns picker's list: a tracking column only when the organisation tracks that category. */
  get columnPickerOptions(): ColumnOption[] {
    const tracking = this.selectedTrackingCategories || [];
    const options = (this.columnOptions || []).filter(o =>
      (o.key !== 'tracking1' || !!tracking[0]) && (o.key !== 'tracking2' || !!tracking[1]));
    const key = options.map(o => o.key + ':' + o.label).join('|');
    if (key !== this._columnPickerKey) {
      this._columnPickerKey = key;
      this._columnPickerOptions = options;
    }
    return this._columnPickerOptions;
  }
  private _columnPickerKey = '';
  private _columnPickerOptions: ColumnOption[] = [];

  /** Handed to the Columns picker, which asks per column. */
  readonly isColumnVisibleFn = (key: string) => this.isColumnVisible(key);

  /** A checkbox in the Columns picker. */
  setColumnVisible(columnKey: string, visible: boolean): void {
    if (!columnKey) return;
    if (columnKey === 'submitter') {
      this.toggleSubmitterColumn(visible);
      return;
    }
    this.columnVisibility = {
      ...this.columnVisibility,
      [columnKey]: visible
    };
    this.persistColumnVisibility();
  }

  isColumnVisible(columnKey: string): boolean {
    if (columnKey === 'submitter') {
      return this.showSubmitterColumn;
    }
    const value = this.columnVisibility[columnKey];
    return value === undefined ? true : value;
  }

  private initializeColumnVisibility(): void {
    const stored = localStorage.getItem(this.columnVisibilityStorageKey);
    if (stored) {
      try {
        this.columnVisibility = JSON.parse(stored) || {};
      } catch {
        this.columnVisibility = {};
      }
    }
    const updatedVisibility: Record<string, boolean> = {};
    this.columnOptions.forEach(option => {
      if (option.key === 'submitter') {
        updatedVisibility[option.key] = this.showSubmitterColumn;
        return;
      }
      const savedValue = this.columnVisibility[option.key];
      updatedVisibility[option.key] = savedValue === undefined ? true : savedValue;
    });
    this.columnVisibility = updatedVisibility;
    this.persistColumnVisibility();
  }

  private persistColumnVisibility(): void {
    localStorage.setItem(this.columnVisibilityStorageKey, JSON.stringify(this.columnVisibility));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(_event: MouseEvent): void {
    this.viewMenuOpen = false;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (this.viewMenuOpen) {
      event.stopPropagation();
      this.viewMenuOpen = false;
    }
  }

  // Debounced pipeline for the free-text inbox search. Keeps the bound text in
  // sync immediately (so the input + clear button stay responsive) but only
  // applies the filter/API call after the user pauses typing.
  private setInboxSearch(): void {
    this._inboxSearch$
      .pipe(this.untilThis, debounceTime(350), distinctUntilChanged())
      .subscribe(() => this.applyInboxFilters());
  }

  // Search filter toolbar event handlers
  onInboxSearchChanged(searchText: string): void {
    this.inboxSearchText = searchText;       // immediate — drives the input binding
    this._inboxSearch$.next(searchText);     // debounced — triggers the actual search
  }

  onInboxSearchCleared(): void {
    this.inboxSearchText = '';
    // Clearing should feel instant, so apply right away rather than waiting on
    // the debounce. A keystroke queued in the last ~350ms would re-run the
    // filter once more, but it reads the now-empty text and is cancelled by the
    // single-flight fetch, so the result is the same.
    this.applyInboxFilters();
  }

  onInboxFilterChanged(filterValue: string): void {
    this.selectedInboxStatusFilter = filterValue;
    this.loading = true;
    // Switch tabs and load appropriate data based on filter selection
    if (filterValue === 'for-review') {
      this._location.replaceState('/inbox?isProcessed=false');
      this.isInProcessingTab = true;
      this.isProcessedTab = false;
      this.isProcessingError = false;
      this.getInProcessingInvoices();
    } else if (filterValue === 'processed') {
      this._location.replaceState('/inbox?isProcessed=true');
      this.isInProcessingTab = false;
      this.isProcessedTab = true;
      this.isProcessingError = false;
      this.getProcessedInvoicesForUser();
    } else if (filterValue === 'all') {
      // For 'all', show the current tab or default to InProcessing
      if (!this.isInProcessingTab && !this.isProcessedTab) {
        this.isInProcessingTab = true;
        this.isProcessedTab = false;
        this.isProcessingError = false;
        this.getInProcessingInvoices();
      }
    }
  }

  private applyInboxFilters(): void {
    // When a filter is active, re-run the unified search — getSearchState
    // builds the query with both the filter criteria and the free-text
    // `search`, so the two combine instead of the search dropping the filter.
    if (this.isFilterShown && this.currentFilterCriteria) {
      this.getSearchState(this.currentFilterCriteria);
      return;
    }
    // Unfiltered — apply the search to the current tab's data.
    if (this.isCombinedTab) {
      this.applyFiltersToCombined();
    } else if (this.isInProcessingTab) {
      this.applyFiltersToInProcessing();
    } else if (this.isProcessedTab) {
      this.applyFiltersToProcessed();
    }
  }

  private applyFiltersToInProcessing(): void {
    let filtered = [...(this.inProcessingInvoices || [])];

    // Apply search filter
    if (this.inboxSearchText && this.inboxSearchText.trim() !== '') {
      const searchTerm = this.inboxSearchText.toLowerCase().trim();
      filtered = filtered.filter(invoice =>
        invoice.supplierName?.toLowerCase().includes(searchTerm) ||
        invoice.invoiceNumber?.toLowerCase().includes(searchTerm) ||
        invoice.submitterName?.toLowerCase().includes(searchTerm)
      );
    }

    this.filteredInProcessingInvoices = filtered;
  }

  private applyFiltersToProcessed(): void {
    this.searchText = this.inboxSearchText;
    this.processedPage = 1;
    this.getProcessedInvoices();
  }

  // True when the "Filters Applied" indicator should be visible: an inline /
  // header / saved filter criterion is actually set. The inbox URL always
  // carries `isProcessed`/`status`/paging params, so the presence of a query
  // string is NOT a reliable signal — we must inspect the parsed criteria.
  private shouldShowFilterIndicator(): boolean {
    const p = this.filterParams;
    const arr = (v: any) => Array.isArray(v) && v.length > 0;
    const hasParamFilter = !!p && (
      arr(p.suppliers) || arr(p.categories) || arr(p.tags) ||
      arr(p.trackingOptions1) || arr(p.trackingOptions2) ||
      arr(p.submitters) || arr(p.assignedTo) || arr(p.types) ||
      arr(p.invoiceDateRange) ||
      (p.amountMin !== null && p.amountMin !== undefined) ||
      (p.amountMax !== null && p.amountMax !== undefined)
    );
    return hasParamFilter
      || (this.selectedSavedFilters || []).some(f => f.isSelectedTab && f.name !== 'All');
  }

  // Filter status management methods
  getActiveFilterName(): string {
    // If we have a saved filter selected, show its name
    const activeFilter = this.selectedSavedFilters.find(f => f.isSelectedTab);
    if (activeFilter && activeFilter.name !== 'All') {
      return `Filter: ${activeFilter.name}`;
    }

    // Any applied filter (field criteria or status) shows "Filter Applied".
    if (this.hasActiveUnsavedFilters || this.isFilterShown) {
      return "Filter Applied";
    }

    return "No filters";
  }

  clearAllFilters(): void {
    // Reset all filter states
    this.isFilterShown = false;
    this.hasActiveUnsavedFilters = false;
    this.currentFilterCriteria = "";
    this.filterStr = "";
    this.selectedFilterId = "";

    // Clear selected saved filters
    this.selectedSavedFilters.forEach(x => x.isSelectedTab = false);
    this.selectedSavedFilters = [this.defaultFilter];
    this.defaultFilter.isSelectedTab = true;
    this.currentFilterCriteria = this.defaultFilter.searchCriteria || '';
    this.inboxFilterComponent?.clear();
    this.filterParams = new BookkeepingFilter();

    // Clear the toolbar search text before reloading so the fetch is unfiltered.
    // The status view (For Review / Processed / All) is its own control and
    // stays as it is.
    this.inboxSearchText = '';
    this.applyStatusPills();
  }

  editCurrentFilter(): void {
    this.isEditMode = true;
    this.showFilterPanel = true;
    const saved = this.activeSavedFilter;
    setTimeout(() => {
      if (saved) {
        this.inboxFilterComponent?.openForEdit(saved);
      } else {
        this.inboxFilterComponent?.openForCriteria(this.currentFilterCriteria);
      }
    });
  }

  // ── Title picker: status views + saved filters (as on Bills to Pay) ──────
  viewMenuOpen: boolean = false;

  readonly statusViews: InboxStatusView[] = [
    { key: 'for-review', name: 'For Review', blurb: 'Documents waiting to be checked and published', icon: 'fa-inbox' },
    { key: 'processed', name: 'Processed', blurb: 'Documents already published', icon: 'fa-check-circle-o' },
    { key: 'all', name: 'All documents', blurb: 'For Review and Processed together', icon: 'fa-files-o' }
  ];

  get activeStatusView(): InboxStatusView {
    const key: InboxStatusViewKey = this.pillForReview && this.pillProcessed ? 'all'
      : this.pillProcessed ? 'processed' : 'for-review';
    return this.statusViews.find(v => v.key === key);
  }

  get documentCountLabel(): string {
    const count = this.inboxFilteredCount;
    return `${count} ${count === 1 ? 'document' : 'documents'}`;
  }

  toggleViewMenu(event: MouseEvent): void {
    // Otherwise the document click listener closes it in the same tick.
    event.stopPropagation();
    this.viewMenuOpen = !this.viewMenuOpen;
  }

  closeViewMenu(): void {
    this.viewMenuOpen = false;
  }

  selectStatusView(key: InboxStatusViewKey): void {
    if (key === this.activeStatusView.key) return;

    if (key === 'all') {
      this.pillForReview = true;
      this.pillProcessed = true;
      this.combinedPage = 1;
      this.processedPage = 1;
      this.applyStatusPills();
    } else {
      this.toggleStatusPill(key === 'processed' ? 'processed' : 'forReview');
    }
  }

  private get activeSavedFilter(): ReportFilter {
    return this.selectedFilterId
      ? (this.savedFilters || []).find(f => f.id === this.selectedFilterId && f.name !== 'All')
      : null;
  }

  private get inboxFieldLabels() {
    return {
      job: this.selectedTrackingCategories?.[0]?.name,
      job2: this.selectedTrackingCategories?.[1]?.name
    };
  }

  /** Rebuilt only when the list or the active filter changes — the menu reads it every tick. */
  private savedFilterCardsKey: string = null;
  private savedFilterCardsCache: InboxSavedFilterCard[] = [];

  get savedFilterCards(): InboxSavedFilterCard[] {
    const saved = (this.savedFilters || []).filter(f => !!f.id && f.name !== 'All');
    const activeId = this.isFilterShown ? this.selectedFilterId : '';
    const key = JSON.stringify([saved.map(f => [f.id, f.name, f.searchCriteria]), activeId, this.inboxFieldLabels]);

    if (key !== this.savedFilterCardsKey) {
      this.savedFilterCardsKey = key;
      this.savedFilterCardsCache = saved.map(filter => {
        const rule = describeInboxCriteria(filter.searchCriteria, this.inboxFieldLabels) || 'No conditions';
        return {
          id: filter.id,
          name: filter.name,
          icon: iconForInboxConditions(inboxCriteriaToConditions(filter.searchCriteria)),
          rule,
          blurb: inboxDescriptionFromCriteria(filter.searchCriteria) || rule,
          active: !!activeId && activeId === filter.id,
          filter
        };
      });
    }
    return this.savedFilterCardsCache;
  }

  trackCardById(_index: number, card: InboxSavedFilterCard): string {
    return card.id;
  }

  /** The chip's bold part: the saved filter's name, or "Filter" for an unsaved one. */
  get activeFilterName(): string {
    return this.activeSavedFilter?.name || 'Filter';
  }

  get activeFilterRule(): string {
    const criteria = this.activeSavedFilter?.searchCriteria || this.currentFilterCriteria;
    return describeInboxCriteria(criteria, this.inboxFieldLabels);
  }

  openNewInboxFilter(): void {
    this.showFilterPanel = true;
    this.isEditMode = false;
    setTimeout(() => this.inboxFilterComponent?.openForNew());
  }

  applySavedInboxFilter(saved: ReportFilter): void {
    this.showFilterPanel = false;
    this.savedFilters.forEach(x => x.isSelectedTab = false);
    this.defaultFilter.isSelectedTab = false;
    saved.isSelectedTab = true;
    this.selectedSavedFilters = [this.defaultFilter, saved];
    this.hasActiveUnsavedFilters = false;
    this.currentFilterCriteria = saved.searchCriteria || '';
    this.filterStr = this.currentFilterCriteria;
    // getSearchState reads the filter id from the criteria, which a saved
    // filter's criteria never carry — set it before and after.
    this.selectedFilterId = saved.id;
    this.applyStatusFromCriteria(this.currentFilterCriteria);
    this.getSearchState(this.currentFilterCriteria);
    this.selectedFilterId = saved.id;
    this.isFilterShown = true;
  }

  /** "Apply filters" in the panel — an unsaved filter. */
  applyInboxFilterCriteria(criteria: string): void {
    this.showFilterPanel = false;
    this.savedFilters.forEach(x => x.isSelectedTab = false);
    this.selectedSavedFilters = [this.defaultFilter];
    this.defaultFilter.isSelectedTab = true;
    this.selectedFilterId = '';
    this.hasActiveUnsavedFilters = true;
    this.currentFilterCriteria = criteria;
    this.filterStr = criteria;
    this.applyStatusFromCriteria(criteria);
    this.getSearchState(criteria);
    this.selectedFilterId = '';
  }

  /**
   * A filter's Status condition switches the list — the same switch the title
   * picker makes — before the search runs, since the search reads the status
   * from the active view. No Status condition leaves the view as it is.
   */
  private applyStatusFromCriteria(criteria: string): void {
    const status = statusesFromCriteria(criteria);
    if (!status || (!status.forReview && !status.processed)) return;

    this.pillForReview = status.forReview;
    this.pillProcessed = status.processed;
    this.isCombinedTab = status.forReview && status.processed;
    this.isProcessedTab = !status.forReview && status.processed;
    this.isInProcessingTab = status.forReview && !status.processed;
    this.selectedInboxStatusFilter = status.processed && !status.forReview ? 'processed' : 'for-review';
    this.combinedPage = 1;
    this.processedPage = 1;
  }

  onInboxFilterSaved(filter: ReportFilter): void {
    const index = this.savedFilters.findIndex(f => f.id === filter.id);
    if (index > -1) {
      this.savedFilters[index] = filter;
    } else {
      this.savedFilters = this.savedFilters.concat([filter]);
    }
    this.applySavedInboxFilter(filter);
  }

  onInboxFilterDeleted(id: string): void {
    this.savedFilters = this.savedFilters.filter(f => f.id !== id);
    this.selectedSavedFilters = this.selectedSavedFilters.filter(f => f.id !== id);
    this.showFilterPanel = false;
    if (this.selectedFilterId === id) {
      this.clearAllFilters();
    }
  }

  saveCurrentFilter(): void {
    // Open the filter sidebar and trigger save dialog
    this.showSaveFilterDialog = true;

    // We'll need to communicate with the filter sidebar to show save dialog
    // This will be handled by the filter sidebar component when it opens
    setTimeout(() => {
      // Emit an event or set a flag that the filter sidebar can detect
      // For now, we'll rely on the user clicking the Save button in the sidebar
    }, 100);
  }

  // Update filter application tracking
  updateFilterApplication(filterCriteria: string, hasActiveFilters: boolean,
    status?: { forReview: boolean, processed: boolean }): void {
    // Apply the panel's Status selection (deferred — applied here on Apply,
    // not on change). getSearchState reads these flags to pick the status.
    const statusApplied = !!(status && (status.forReview || status.processed));
    if (statusApplied) {
      this.pillForReview = status.forReview;
      this.pillProcessed = status.processed;
      this.isCombinedTab = status.forReview && status.processed;
      this.isProcessedTab = !status.forReview && status.processed;
      this.isInProcessingTab = status.forReview && !status.processed;
    }
    const hasCriteria = !!(filterCriteria && filterCriteria !== '' && hasActiveFilters);
    // Any apply from the panel — field filters OR a status-only change — counts
    // as a "Custom Filter Applied": shows the badge and hides the toolbar pills.
    // (isFilterShown is recomputed by getSearchState from the actual criteria.)
    if (hasCriteria || statusApplied) {
      this.hasActiveUnsavedFilters = !this.selectedSavedFilters.some(f => f.isSelectedTab && f.name !== 'All');
    }
    if (hasCriteria) {
      this.currentFilterCriteria = filterCriteria;
      this.filterStr = filterCriteria;
    }
    // Run the unified search. With field filters → use the criteria; for a
    // status-only change there are no field criteria, so pass '' — getSearchState
    // still fetches, carrying just the new status.
    if (hasCriteria) {
      this.getSearchState(filterCriteria);
    } else if (statusApplied) {
      this.getSearchState('');
    }
  }

  ngOnDestroy() {
    this.stopListeningForFileDrops();
    this.subscriptions.forEach(s => s.unsubscribe());
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.infiniteScrollObserver?.disconnect();
    this._detailOverlayRef?.dispose();
  }

  // Cancel save filter dialog
  cancelSaveFilter() {
    this.showSaveFilterDialog = false;
    this.newFilterName = "";
  }

  newFilterName: string = "";
  filterName: string = "";
  showSaveFilterDialog: boolean = false;
  isPinned: boolean = false;
  filterUrlPattern: string = '';
  userDetails: any;
  // Confirm save filter with entered name
  confirmSaveFilter() {
    if (this.newFilterName && this.newFilterName.trim() !== '') {
      const originalFilterName = this.filterName;
      this.filterName = this.newFilterName.trim();
      this.SaveFilter();
      this.showSaveFilterDialog = false;
      this.newFilterName = "";
      this.hasActiveUnsavedFilters = false;
    }
  }

  SaveFilter() {
    let filter = {
      id: this.selectedFilterId,
      name: this.filterName,
      type: 0,
      isPinned: this.isPinned,
      searchCriteria: this.filterUrlPattern,
      createdBy: this.userDetails.id,
      createdDate: new Date(),
      filterPageType: 1,
      organizationId: this._organizationService.selectedOrganization
    } as ReportFilter;

    this._filterService.SaveFilter("", filter)
      .pipe(take(1))
      .subscribe(
        (createdFilter: string) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, "Success", "Filter Saved Successfully.");
          filter.id = createdFilter;
          this.selectedSavedFilters.forEach(x => x.isSelectedTab = false);
          filter.isSelectedTab = true;
          this.selectedSavedFilters.push(filter);
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  checkIfMobileDevice() {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || environment.isOutlookEnv || environment.isChromeEnv) {
      // true for mobile device
      this.isMobileDevice = true;
    } else {
      this.isMobileDevice = false;
    }
  }

  toggleExpandFilters(event) {
    this.isFilterExpanded = event;
  }

  // Result counters for the inbox toolbar — resolve to whichever tab(s) are
  // currently active (combined / for-review / processed).
  get inboxTotalCount(): number {
    return this.isCombinedTab ? (this.combinedTotalAmount || 0)
      : this.isInProcessingTab ? (this.inProcessingInvoices?.length || 0)
      : (this.processedInvoices?.length || 0);
  }

  get inboxFilteredCount(): number {
    return this.isCombinedTab ? (this.filteredCombinedInvoices?.length || 0)
      : this.isInProcessingTab ? (this.filteredInProcessingInvoices?.length || 0)
      : (this.filteredProcessedInvoices?.length || 0);
  }

  // ── Infinite scroll ──
  // Observes a sentinel below the list; when it scrolls into view the next
  // page is fetched and appended. Covers the Combined view and the unfiltered
  // Processed view — the filtered Processed view keeps its pager.
  ngAfterViewInit(): void {
    this.infiniteScrollObserver = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) { this.onInfiniteScroll(); }
      },
      { rootMargin: '250px' }
    );
    if (this.infiniteScrollAnchor) {
      this.infiniteScrollObserver.observe(this.infiniteScrollAnchor.nativeElement);
    }

    // Deep-link (?id=<guid>): open the detail overlay now that the template ref
    // exists. Deferred a tick so the attach doesn't run inside this CD pass.
    if (this._pendingDeepLinkId) {
      const id = this._pendingDeepLinkId;
      this._pendingDeepLinkId = null;
      setTimeout(() => this.openInvoiceOverlay({ id, editMode: this.overlayEditMode }));
    }
  }

  get combinedHasMore(): boolean {
    return (this.combinedInvoices?.length || 0) < (this.combinedTotalAmount || 0);
  }

  get processedHasMore(): boolean {
    return (this.processedInvoices?.length || 0) < (this.processedTotalAmount || 0);
  }

  private onInfiniteScroll(): void {
    // Filtered views load their full matching set in one call (see
    // getSearchState), so there is nothing to append on scroll.
    if (this.loading || this.loadingMore || this.isFilterShown) { return; }
    if (this.isCombinedTab) {
      if (!this.combinedHasMore) { return; }
      this.combinedPage++;
      this.getCombinedInvoices(true);
    } else if (this.isProcessedTab) {
      if (!this.processedHasMore) { return; }
      this.processedPage++;
      this.getProcessedInvoices(true);
    }
  }

  createExpense() {
    this.droppedFiles = null;
    this.isUploadSidebar = true;
    this.isSidebarVisible = true;
  }

  // ── Drop a document anywhere on the Inbox ────────────────────────────────
  // Dragging a file over the page shows a drop target over it; letting go opens
  // the upload panel with the file already loaded, as the dashboard's Expenses
  // card does. Listened to on the window, outside Angular: dragover fires
  // continuously, and only the start and end of a drag change anything.

  private readonly onWindowDragEnter = (event: DragEvent) => {
    if (!this.acceptsFileDrop(event)) { return; }
    event.preventDefault();
    this.fileDragDepth++;
    if (!this.isFileDragActive) { this._zone.run(() => this.isFileDragActive = true); }
  };

  private readonly onWindowDragOver = (event: DragEvent) => {
    if (!this.acceptsFileDrop(event)) { return; }
    // Without this the browser opens the file in the tab instead of dropping it here.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  private readonly onWindowDragLeave = () => {
    if (!this.isFileDragActive) { return; }
    // Every element entered is later left; back at zero, the drag has left the window.
    this.fileDragDepth = Math.max(0, this.fileDragDepth - 1);
    if (!this.fileDragDepth) { this.endFileDrag(); }
  };

  private readonly onWindowDrop = (event: DragEvent) => {
    const wasActive = this.isFileDragActive;
    this.endFileDrag();
    // The upload panel's own drop zone has taken it already.
    if (event.defaultPrevented || !wasActive || !this.acceptsFileDrop(event)) { return; }
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length) { this._zone.run(() => this.openUploadWithFiles(files)); }
  };

  private listenForFileDrops(): void {
    this._zone.runOutsideAngular(() => {
      window.addEventListener('dragenter', this.onWindowDragEnter);
      window.addEventListener('dragover', this.onWindowDragOver);
      window.addEventListener('dragleave', this.onWindowDragLeave);
      window.addEventListener('drop', this.onWindowDrop);
    });
  }

  private stopListeningForFileDrops(): void {
    window.removeEventListener('dragenter', this.onWindowDragEnter);
    window.removeEventListener('dragover', this.onWindowDragOver);
    window.removeEventListener('dragleave', this.onWindowDragLeave);
    window.removeEventListener('drop', this.onWindowDrop);
  }

  /** Only files, and not while a bill is open over the page: a drop there would land
   *  on the bill's own document rather than start a new one. */
  private acceptsFileDrop(event: DragEvent): boolean {
    const types = event.dataTransfer && event.dataTransfer.types;
    const isFile = !!types && Array.prototype.indexOf.call(types, 'Files') >= 0;
    return isFile && !this._detailOverlayRef?.hasAttached();
  }

  private endFileDrag(): void {
    this.fileDragDepth = 0;
    if (this.isFileDragActive) { this._zone.run(() => this.isFileDragActive = false); }
  }

  private openUploadWithFiles(files: File[]): void {
    // A new reference each time, so a second drop onto an open panel reaches it too.
    this.droppedFiles = files;
    if (!this.isUploadSidebar || !this.isSidebarVisible) {
      this.isSettingsSidebar = false;
      this.isUploadSidebar = true;
      this.isSidebarVisible = true;
    }
  }

    subscribeToAddingInvoice() {
      this._recognizedInvoiceService.inProcessingRecordState$
        .pipe(this.untilThis)
        .subscribe(
          (invoice: InvoiceShort) => {
            this.filteredInProcessingInvoices = [invoice, ...this.filteredInProcessingInvoices];
          },
          (e: Error) => {
            this._toasterMessageService.displayToasterState(
              ToasterTypes.Error,
              'Error',
              e.message
            );
          }
        );
    }

    /**
     * An invoice finished processing. Re-run the current search so it appears.
     *
     * The table already listens to this stream, but its handler (_addNewInvoice) only ever
     * REPLACES the in-processing placeholder row — `lastIndexOf(inProcessingRecord)` returning -1
     * makes it a silent no-op. That works for the tab that did the uploading, which saw
     * InvoiceInProcessing first and so has a placeholder to swap. It does nothing at all for an
     * invoice that arrives any other way — email ingestion, another tab, another device — which is
     * why the inbox went stale while the dashboard, whose handler simply re-fetches, stayed
     * current.
     *
     * Reloading through getSearchState rather than splicing the invoice in locally is deliberate:
     * the list is filtered, sorted and paged SERVER-side, so a row inserted on the client would
     * ignore the active filter and sit in the wrong position. Re-running the search is the only
     * way the result stays something the server would actually have returned.
     */
    subscribeToProcessedInvoices() {
      this._recognizedInvoiceService.invoiceState$
        .pipe(this.untilThis)
        .subscribe(
          () => this.reloadCurrentInboxView(),
          () => { /* a failed refresh must not toast over the user's work; the list simply waits */ }
        );
    }

    /** Re-run whichever load backs the view on screen. Mirrors the dispatch used when the status
     *  filter changes — an active filter goes through the search endpoint, and each tab otherwise
     *  has its own loader, so refreshing via the wrong one would quietly swap the user's view. */
    private reloadCurrentInboxView() {
      if (this.isFilterShown && this.currentFilterCriteria) {
        this.getSearchState(this.currentFilterCriteria);
      } else if (this.isCombinedTab) {
        this.getCombinedInvoices();
      } else if (this.isProcessedTab) {
        this.getProcessedInvoicesForUser();
      } else {
        this.getInProcessingInvoices();
      }
    }

    /**
     * The open document was saved: show the change in its row now, rather than only after a
     * reload. Every loaded list, as removeInvoiceFromInbox does, since the same row can be in
     * several and filterInvoices() re-derives the filtered ones from the rest.
     */
    updateInboxRow(invoice: Invoice) {
      const id = invoice?.id?.toString();
      if (!id) return;
      const changes: Partial<InvoiceShort> = {
        invoiceNumber: invoice.invoiceNumber,
        supplierName: invoice.supplierName,
        date: invoice.date,
        dueDate: invoice.dueDate,
        amountDue: invoice.amountDue,
        totalTax: invoice.totalTax,
        accountName: invoice.accountName,
        // What the row's Category select is bound to.
        accountId: (invoice as any).accountId,
        status: invoice.status,
        required_Fields: invoice.required_Fields,
      };
      // Only what the form actually holds: a field it never loaded must not blank the row's.
      Object.keys(changes).forEach(k => changes[k] === undefined && delete changes[k]);

      [this.inProcessingInvoices, this.processedInvoices, this.combinedInvoices,
       this.filteredInProcessingInvoices, this.filteredProcessedInvoices, this.filteredCombinedInvoices]
        .forEach(list => Object.values(list ?? [])
          .filter((row: InvoiceShort) => row?.id?.toString() === id)
          .forEach((row: InvoiceShort) => Object.assign(row, changes)));
    }

    removeInvoiceFromInbox(invoiceId?: string) {
      // On publish the form passes the published id (the overlay has already
      // advanced to the next bill, so overlayInvoiceId is no longer it). On
      // document delete no id is passed, so fall back to the open bill.
      const id = invoiceId ?? this.overlayInvoiceId;
      if (!id) return;
      // Drop it from every loaded list so the table reflects reality regardless
      // of which tab/pill is active (filter is a no-op where it isn't present).
      const without = (list: InvoiceShort[]) =>
        Object.values(list ?? []).filter(x => x?.id?.toString() !== id);

      this.filteredInProcessingInvoices = without(this.filteredInProcessingInvoices);
      this.filteredProcessedInvoices = without(this.filteredProcessedInvoices);
      this.filteredCombinedInvoices = without(this.filteredCombinedInvoices);

      // The unfiltered sources too: filterInvoices() re-derives the filtered lists from these,
      // so removing it only from the filtered copies let the deleted row reappear the next time
      // the user touched a filter, a search or a tab.
      this.inProcessingInvoices = without(this.inProcessingInvoices);
      this.processedInvoices = without(this.processedInvoices);
      this.combinedInvoices = without(this.combinedInvoices);
    }
}
